import React, { useState, useEffect } from "react"
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Card,
  Typography,
  IconButton,
  styled,
  Tooltip,
  CircularProgress,
  useTheme,
  Collapse,
  Grid,
  Chip,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  alpha,
  Checkbox,
  Alert,
  AlertTitle,
  Menu,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  FormControlLabel,
  Stack,
} from "@mui/material"
import { Pagination } from "@mui/material"
import {
  Add as AddIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
  Edit,
  Visibility,
  LocalShipping,
  Payment,
  Person,
  LocationOn,
  ShoppingCart,
  Update as UpdateIcon,
  CheckCircle,
  Schedule,
  LocalShippingOutlined,
  Cancel,
  Refresh,
  FileDownload,
  SelectAll,
  Receipt as ReceiptIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  PictureAsPdf as PdfIcon,
  LocalShipping as DeliveryIcon,
  Assignment as ChallanIcon,
  Email as EmailIcon,
  Send as SendIcon
} from "@mui/icons-material"
import { useNavigate } from "react-router-dom"

import apiService from "Network/apiService"
import { API_ENDPOINTS } from "Network/apiEndpoints"

// Enhanced styled components with better dark theme support
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: "16px 20px",
  color: theme.palette.text.primary,
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  whiteSpace: "nowrap",
  "&.header": {
      fontWeight: 600,
      fontSize: "0.875rem",
      backgroundColor: alpha(theme.palette.primary.main, 0.04),
      color: theme.palette.text.primary,
      position: 'sticky',
      top: 0,
      zIndex: 1,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.04),
      transform: 'translateY(-1px)',
      boxShadow: `0 4px 8px ${alpha(theme.palette.common.black, 0.1)}`,
  },
  "& > .MuiTableCell-root": {
      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  },
}));

const ExpandableRow = styled(TableRow)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.background.paper, 0.8),
  '& > .MuiTableCell-root': {
    borderBottom: 'none',
  }
}));

const DetailCard = styled(Card)(({ theme }) => ({
  margin: '8px 0',
  padding: '20px',
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  borderRadius: theme.shape.borderRadius * 2,
  transition: 'all 0.3s ease-in-out',
}));

const StatusChip = styled(Chip)(({ theme, status }) => {
  const getStatusConfig = (status) => {
    const configs = {
      pending: { 
        bg: '#FFB74D',
        text: '#FFFFFF',
        icon: <Schedule sx={{ fontSize: '16px !important' }} />
      },
      confirmed: { 
        bg: '#BA68C8',
        text: '#FFFFFF',
        icon: <CheckCircle sx={{ fontSize: '16px !important' }} />
      },
      processing: { 
        bg: '#BA68C8',
        text: '#FFFFFF',
        icon: <Schedule sx={{ fontSize: '16px !important' }} />
      },
      shipped: { 
        bg: '#66BB6A',
        text: '#FFFFFF',
        icon: <LocalShippingOutlined sx={{ fontSize: '16px !important' }} />
      },
      dispatch: {
        bg: '#66BB6A',
        text: '#FFFFFF',
        icon: <LocalShippingOutlined sx={{ fontSize: '16px !important' }} />
      },
      delivered: { 
        bg: '#66BB6A',
        text: '#FFFFFF',
        icon: <CheckCircle sx={{ fontSize: '16px !important' }} />
      },
      cancelled: { 
        bg: '#EF5350',
        text: '#FFFFFF',
        icon: <Cancel sx={{ fontSize: '16px !important' }} />
      },
      returned: { 
        bg: '#BA68C8',
        text: '#FFFFFF',
        icon: <Refresh sx={{ fontSize: '16px !important' }} />
      },
      orders: {
        bg: '#FFB74D',
        text: '#FFFFFF',
        icon: <Schedule sx={{ fontSize: '16px !important' }} />
      }
    }; 
    return configs[status?.toLowerCase()] || { 
      bg: theme.palette.grey[400], 
      text: theme.palette.common.white,
      icon: null
    };
  };

  const config = getStatusConfig(status);
  return {
    backgroundColor: config.bg,
    color: config.text,
    fontWeight: 600,
    textTransform: 'capitalize',
    borderRadius: theme.shape.borderRadius,
    height: 'auto',
    padding: '4px 10px',
    minWidth: 'auto',
    '& .MuiChip-icon': {
      color: config.text,
      marginLeft: '4px',
      marginRight: '4px'
    },
    '& .MuiChip-label': {
      padding: '0 4px',
      fontSize: '0.875rem',
      color: config.text,
      fontWeight: 600
    }
  };
});

const PaymentStatusChip = styled(Chip)(({ theme, status }) => {
  const getPaymentStatusConfig = (status) => {
    const configs = {
      pending: { 
        bg: '#fcc874',
        text: '#FFFFFF',
        icon: <Schedule sx={{ fontSize: '16px !important' }} />
      },
      outstanding: { 
        bg: '#888cff',
        text: '#FFFFFF',
        icon: <Schedule sx={{ fontSize: '16px !important' }} />
      },
      paid: { 
        bg: '#22d28a',
        text: '#FFFFFF',
        icon: <CheckCircle sx={{ fontSize: '16px !important' }} />
      },
      failed: { 
        bg: '#f4346e',
        text: '#FFFFFF',
        icon: <Cancel sx={{ fontSize: '16px !important' }} />
      },
      refunded: { 
        bg: '#888cff',
        text: '#FFFFFF',
        icon: <Refresh sx={{ fontSize: '16px !important' }} />
      },
    }; 
    return configs[status?.toLowerCase()] || { 
      bg: theme.palette.grey[400], 
      text: '#FFFFFF',
      icon: null
    };
  };

  const config = getPaymentStatusConfig(status);
  return {
    backgroundColor: config.bg,
    color: config.text,
    fontWeight: 600,
    textTransform: 'capitalize',
    borderRadius: theme.shape.borderRadius,
    height: 'auto',
    padding: '4px 10px',
    minWidth: 'auto',
    '& .MuiChip-icon': {
      color: config.text,
      marginLeft: '4px',
      marginRight: '4px'
    },
    '& .MuiChip-label': {
      padding: '0 4px',
      fontSize: '0.875rem',
      color: config.text,
      fontWeight: 600
    }
  };
});

const EmptyStateBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 300,
  padding: theme.spacing(4),
  textAlign: 'center',
  '& .empty-icon': {
    fontSize: 64,
    color: theme.palette.text.disabled,
    marginBottom: theme.spacing(2),
  }
}));

const InvoiceModal = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    maxWidth: '90vw',
    maxHeight: '90vh',
    width: '1200px',
    height: '800px',
    margin: theme.spacing(2),
  },
}));

const AppOrderPopup = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: 8,
    overflow: 'hidden',
    boxShadow: `0 12px 32px ${alpha(theme.palette.common.black, 0.18)}`,
  },
  '& .MuiDialogTitle-root': {
    padding: theme.spacing(2.5, 3, 1.5),
  },
  '& .MuiDialogContent-root': {
    padding: theme.spacing(0, 3, 2.25),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(0, 3, 2.5),
  },
}));

const InvoiceFrame = styled('iframe')(({ theme }) => ({
  width: '100%',
  height: '100%',
  border: 'none',
  borderRadius: theme.shape.borderRadius,
}));

const ProductList = ({ 
  data: products = [], 
  searchQuery = '', 
  statusFilter = 'all', 
  isLoading = false, 
  onRefresh,
  selectedProducts = [],
  onProductSelect,
  onSelectAll,
  isAllSelected = false,
  onExport,
  onZipDownload,
  showCancelRequestActions = false,
  onApproveCancelRequest,
  onRejectCancelRequest,
  updatingCancelRequestOrderId = null,
  showReturnRequestActions = false,
  onApproveReturnRequest,
  onRejectReturnRequest,
  updatingReturnRequestOrderId = null,
  pagination = null,
  currentPage = 1,
  pageSize = 10,
  onPageChange = null,
  onPageSizeChange = null,
  shouldEnableButton,
}) => {
  const [expandedRows, setExpandedRows] = useState(new Set())
  const [statusUpdateModal, setStatusUpdateModal] = useState({ 
    open: false, 
    order: null,
    entityName: '',
    orderBy: '',
    driverName: '',
    vehicleNo: '',
    delivery_date: new Date().toISOString().slice(0,10),
    generateInvoice: false,
    isCreatingChallan: false,
    challanError: '',
    dispatchPersonName: '',
    customDispatchPerson: '',
    useCustomDispatchPerson: false,
    start_km: '',
    end_km: '',
    cancellationReason: '',
    returnReason: ''
  })
  const [entityOptions, setEntityOptions] = useState([])
  const [loadingEntities, setLoadingEntities] = useState(false)
  const [orderByChoices, setOrderByChoices] = useState([])
  const [paymentUpdateModal, setPaymentUpdateModal] = useState({ open: false, order: null, refundAmount: '' })
  const [invoiceModal, setInvoiceModal] = useState({ open: false, invoice: null, order: null })
  const [deliveryChallanModal, setDeliveryChallanModal] = useState({ open: false, challan: null, order: null })
  const [isDownloading, setIsDownloading] = useState(false);
  const [bulkChallanModal, setBulkChallanModal] = useState({
    open: false,
    orderBy: '',
    entityName: '',
    driverName: '',
    vehicleNo: '',
    deliveryDate: new Date().toISOString().slice(0,10),
    generateInvoice: false,
    isCreating: false,
    error: '',
    selectedStatus: '',
    selectedStatusCategory: '',
    emails: [],
    emailInput: '',
    dispatchPersonName: '',
    customDispatchPerson: '',
    useCustomDispatchPerson: false,
    start_km: '',
    end_km: '',
    bulkPaymentStatus: ''
  })
  const [bulkPaymentStatusModal, setBulkPaymentStatusModal] = useState({
    open: false,
    selectedPaymentStatus: '',
    error: '',
    isUpdating: false
  })
  const [bulkEntityOptions, setBulkEntityOptions] = useState([])
  const [loadingBulkEntities, setLoadingBulkEntities] = useState(false)
  const [updatingStatus, setUpdatingStatus] = useState(false)
  const [updatingPayment, setUpdatingPayment] = useState(false)
  const [isUpdatingBulkPayment, setIsUpdatingBulkPayment] = useState(false)
  const [downloadingInvoice, setDownloadingInvoice] = useState(false)
  const [downloadingChallan, setDownloadingChallan] = useState(false)
  const [checkingPaymentOrderId, setCheckingPaymentOrderId] = useState(null)
  const navigate = useNavigate()
  const theme = useTheme()

  const [statusOptions, setStatusOptions] = useState([])
  const [paymentStatusChoices, setPaymentStatusChoices] = useState([])
  const [dispatchPersons, setDispatchPersons] = useState([])
  const [actionsAnchorEl, setActionsAnchorEl] = useState(null)
  const isActionsMenuOpen = Boolean(actionsAnchorEl)
  const openActionsMenu = (e) => setActionsAnchorEl(e.currentTarget)
  const closeActionsMenu = () => setActionsAnchorEl(null)
  const [emailDialog, setEmailDialog] = useState({
    open: false,
    emails: '',
    sendInvoice: true,
    sendDelivery: true,
    isSending: false,
    error: '',
    success: ''
  })
  const [singleEmailDialog, setSingleEmailDialog] = useState({
    open: false,
    order: null,
    emailInput: '',
    emails: [],
    sendInvoice: true,
    sendDelivery: true,
    isSending: false,
    error: '',
    success: ''
  })

  const extractInvoice = (order) => {
    if (!order) return null;
    const inv = order.invoice;
    if (inv && typeof inv === 'object') {
      const path = inv.path || inv.url || inv.file || inv.link;
      if (path) return { ...inv, path };
    }
    const path = order.invoice_path || order.invoice_url || (typeof inv === 'string' ? inv : null);
    if (path) return { id: inv?.id || order.id, path };
    return null;
  };

  const extractDeliveryChallan = (order) => {
    if (!order) return null;
    const dc = order.delivery_challan || order.deliveryChallan || order.challan;
    if (dc && typeof dc === 'object') {
      const path = dc.path || dc.url || dc.file || dc.link;
      if (path) return { ...dc, path };
    }
    const path = order.delivery_challan_path || order.delivery_challan_url || order.challan_url || (typeof dc === 'string' ? dc : null);
    if (path) return { id: dc?.id || order.id, path };
    return null;
  };

  const getIconForCategory = (category) => {
    const key = (typeof category === 'string' ? category : (category?.name ?? '')).toLowerCase()
    switch (key) {
      case 'pending': return <Schedule />
      case 'confirmed': return <CheckCircle />
      case 'processing': return <Schedule />
      case 'shipped': return <LocalShippingOutlined />
      case 'delivered': return <CheckCircle />
      case 'cancelled': return <Cancel />
      case 'returned': return <Refresh />
      default: return null
    }
  }

  useEffect(() => {
    const fetchOrderStatuses = async () => {
      try {
        const res = await apiService.get(API_ENDPOINTS.GET_order_status_choices)
        const list = Array.isArray(res?.data) ? res.data : []
        const options = list
          .sort((a, b) => (a.sequence || 0) - (b.sequence || 0))
          .map(s => ({
            id: s.id,
            value: s.name || '',
            name: s.name || '',
            label: s.name || '',
            category: s.category || s.name || '',
            sequence: s.sequence || 0
          }))
        setStatusOptions(options)
      } catch (e) {
        console.error('Error fetching order status choices:', e)
      }
    }
    fetchOrderStatuses()
  }, [])

  useEffect(() => {
    const fetchDispatchPersons = async () => {
      try {
        const res = await apiService.get(API_ENDPOINTS.GET_dispatch_persons)
        const list = Array.isArray(res?.data?.DispatchPerson) ? res.data.DispatchPerson : []
        const filteredList = list.filter(person => !person.is_deleted)
        setDispatchPersons(filteredList)
      } catch (e) {
        console.error('Error fetching dispatch persons:', e)
      }
    }
    fetchDispatchPersons()
  }, [])

  useEffect(() => {
    const fetchOrderByChoices = async () => {
      try {
        const res = await apiService.get(API_ENDPOINTS.GET_order_by_choices)
        if (res?.success && Array.isArray(res?.data)) {
          setOrderByChoices(res.data)
        } else {
          console.error('Invalid response format from order by choices API')
          setOrderByChoices([])
        }
      } catch (e) {
        console.error('Error fetching order by choices:', e)
        setOrderByChoices([])
      }
    }
    fetchOrderByChoices()
  }, [])

  useEffect(() => {
    const fetchPaymentStatusChoices = async () => {
      try {
        const res = await apiService.get(API_ENDPOINTS.GET_payment_status_choices)
        if (res?.success && Array.isArray(res?.data)) {
          setPaymentStatusChoices(res.data)
        } else {
          console.error('Invalid response format from payment status choices API')
          setPaymentStatusChoices([])
        }
      } catch (e) {
        console.error('Error fetching payment status choices:', e)
        setPaymentStatusChoices([])
      }
    }
    fetchPaymentStatusChoices()
  }, [])

  const paymentStatusOptions = [
    { value: 'pending', label: 'Pending', icon: <Schedule /> },
    { value: 'outstanding', label: 'Outstanding', icon: <Schedule /> },
    { value: 'paid', label: 'Paid', icon: <CheckCircle /> },
    { value: 'failed', label: 'Failed', icon: <Cancel /> },
    { value: 'refunded', label: 'Refunded', icon: <Refresh /> },
  ]

  const getAvailableCounts = () => {
    const selected = filteredData.filter(order => selectedProducts.includes(order.id))
    const invoices = selected.filter(order => !!extractInvoice(order)?.path).length
    const challans = selected.filter(order => !!extractDeliveryChallan(order)?.path).length
    return { invoices, challans }
  }

  const shouldDisableDocumentsAndEmail = () => {
    const selected = filteredData.filter(order => selectedProducts.includes(order.id))
    const restrictedStatuses = ['Placed', 'Confirmed', 'Processing']
    return selected.some(order => {
      const statusName = order.status?.name || order.status_name || ''
      return restrictedStatuses.includes(statusName)
    })
  }

  const handleOpenEmailDialog = () => {
    setEmailDialog(prev => ({ ...prev, open: true, error: '', success: '' }))
    closeActionsMenu()
  }

  const handleCloseEmailDialog = () => {
    setEmailDialog({ open: false, emails: '', sendInvoice: true, sendDelivery: true, isSending: false, error: '', success: '' })
  }

  const handleEmailDialogChange = (field, value) => {
    setEmailDialog(prev => ({ ...prev, [field]: value, error: '', success: '' }))
  }

  const handleEmailSendSubmit = async () => {
    const { emails, sendInvoice, sendDelivery } = emailDialog
    const userId = localStorage.getItem('user_id')
    const selectedOrderIds = filteredData
      .filter(order => selectedProducts.includes(order.id))
      .map(order => order.order_id)
      .filter(Boolean)

    const trimmed = (emails || '').split(/[,\s]+/).map(e => e.trim()).filter(Boolean)
    if (trimmed.length === 0) {
      setEmailDialog(prev => ({ ...prev, error: 'Please enter at least one email address' }))
      return
    }
    if (!sendInvoice && !sendDelivery) {
      setEmailDialog(prev => ({ ...prev, error: 'Please select at least one document type to send' }))
      return
    }
    const { invoices, challans } = getAvailableCounts()
    if ((sendInvoice && invoices === 0) && (sendDelivery && challans === 0)) {
      setEmailDialog(prev => ({ ...prev, error: 'No available documents to send for selected orders' }))
      return
    }

    setEmailDialog(prev => ({ ...prev, isSending: true, error: '', success: '' }))
    try {
      const payload = {
        emails: trimmed.join(','),
        order_id: selectedOrderIds.join(','),
        user_id: userId,
        invoice: !!sendInvoice,
        delivery: !!sendDelivery
      }

      const res = await apiService.post(
        API_ENDPOINTS.POST_send_challan_on_email,
        JSON.stringify(payload),
        'application/json'
      )

      if (res?.success) {
        setEmailDialog(prev => ({ ...prev, success: 'Email sent successfully', isSending: false }))
        setTimeout(() => handleCloseEmailDialog(), 1200)
      } else {
        throw new Error(res?.message || 'Failed to send email')
      }
    } catch (err) {
      setEmailDialog(prev => ({ ...prev, error: err?.message || 'Failed to send email', isSending: false }))
    }
  }

  const openSingleOrderEmail = (order) => {
    const hasInvoice = !!extractInvoice(order)?.path
    const hasChallan = !!extractDeliveryChallan(order)?.path
    setSingleEmailDialog({
      open: true,
      order: order,
      emailInput: '',
      emails: [],
      sendInvoice: hasInvoice,
      sendDelivery: hasChallan,
      isSending: false,
      error: '',
      success: ''
    })
  }

  const closeSingleOrderEmail = () => {
    setSingleEmailDialog({
      open: false,
      order: null,
      emailInput: '',
      emails: [],
      sendInvoice: true,
      sendDelivery: true,
      isSending: false,
      error: '',
      success: ''
    })
  }

  const addEmailToSingle = () => {
    const input = (singleEmailDialog.emailInput || '').trim()
    if (!input) return
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(input)) {
      setSingleEmailDialog(prev => ({ ...prev, error: 'Enter a valid email address' }))
      return
    }
    if (singleEmailDialog.emails.includes(input)) {
      setSingleEmailDialog(prev => ({ ...prev, error: 'Email already added' }))
      return
    }
    setSingleEmailDialog(prev => ({ ...prev, emails: [...prev.emails, input], emailInput: '', error: '' }))
  }

  const removeEmailFromSingle = (email) => {
    setSingleEmailDialog(prev => ({ ...prev, emails: prev.emails.filter(e => e !== email) }))
  }

  const submitSingleOrderEmail = async () => {
    const { order, emails, sendInvoice, sendDelivery } = singleEmailDialog
    if (!order) return
    if (emails.length === 0) {
      setSingleEmailDialog(prev => ({ ...prev, error: 'Please add at least one email' }))
      return
    }
    if (!sendInvoice && !sendDelivery) {
      setSingleEmailDialog(prev => ({ ...prev, error: 'Please select at least one document type' }))
      return
    }
    const hasInvoice = !!extractInvoice(order)?.path
    const hasChallan = !!extractDeliveryChallan(order)?.path
    if ((sendInvoice && !hasInvoice) && (sendDelivery && !hasChallan)) {
      setSingleEmailDialog(prev => ({ ...prev, error: 'Selected documents are not available for this order' }))
      return
    }

    setSingleEmailDialog(prev => ({ ...prev, isSending: true, error: '', success: '' }))
    try {
      const userId = localStorage.getItem('user_id')
      const payload = {
        emails: emails.join(','),
        order_id: order.order_id,
        user_id: userId,
        invoice: !!sendInvoice,
        delivery: !!sendDelivery
      }
      const res = await apiService.post(
        API_ENDPOINTS.POST_send_challan_on_email,
        JSON.stringify(payload),
        'application/json'
      )
      if (res?.success) {
        setSingleEmailDialog(prev => ({ ...prev, success: 'Email sent successfully', isSending: false }))
        setTimeout(() => closeSingleOrderEmail(), 1200)
      } else {
        throw new Error(res?.message || 'Failed to send email')
      }
    } catch (err) {
      setSingleEmailDialog(prev => ({ ...prev, error: err?.message || 'Failed to send email', isSending: false }))
    }
  }

  const handleViewInvoice = (order) => {
    const inv = extractInvoice(order)
    if (inv && inv.path) {
      setInvoiceModal({
        open: true,
        invoice: inv,
        order: order
      })
    }
  }

  const handleDownloadInvoice = async (invoice, orderName) => {
    if (!invoice || !invoice.path) {
      alert('Invoice not available for download')
      return
    }

    setDownloadingInvoice(true)
    try {
      const link = document.createElement('a')
      link.href = invoice.path
      link.download = `Invoice_${invoice.id}_${orderName.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`
      link.target = '_blank'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (error) {
      console.error('Error downloading invoice:', error)
      alert('Error downloading invoice. Please try again.')
    } finally {
      setDownloadingInvoice(false)
    }
  }

  const handleCloseInvoiceModal = () => {
    setInvoiceModal({ open: false, invoice: null, order: null })
  }

  const handleViewDeliveryChallan = (order) => {
    const dc = extractDeliveryChallan(order)
    if (dc && dc.path) {
      setDeliveryChallanModal({
        open: true,
        challan: dc,
        order: order
      })
    }
  }

  const handleCheckPaymentStatus = async (order) => {
    const orderId = order?.order_id ?? order?.id
    if (!orderId) return
    const userId = localStorage.getItem('user_id') || ''
    setCheckingPaymentOrderId(orderId)
    try {
      const url = `${API_ENDPOINTS.Refund_button_click}?user_id=${encodeURIComponent(userId)}&order_id=${encodeURIComponent(orderId)}`
      const response = await apiService.get(url)
      if (response?.success && onRefresh) {
        await onRefresh()
      } else if (!response?.success) {
        alert(response?.message || 'Check payment status failed.')
      }
    } catch (error) {
      console.error('Check payment status error:', error)
      alert(error?.message || 'Check payment status failed.')
    } finally {
      setCheckingPaymentOrderId(null)
    }
  }

  const handleDownloadDeliveryChallan = async (challan, orderName) => {
    if (!challan || !challan.path) {
      alert('Delivery challan not available for download')
      return
    }

    setDownloadingChallan(true)
    try {
      const link = document.createElement('a')
      link.href = challan.path
      link.download = `Delivery_Challan_${challan.id}_${orderName.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`
      link.target = '_blank'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (error) {
      console.error('Error downloading delivery challan:', error)
      alert('Error downloading delivery challan. Please try again.')
    } finally {
      setDownloadingChallan(false)
    }
  }

  const handleCloseDeliveryChallanModal = () => {
    setDeliveryChallanModal({ open: false, challan: null, order: null })
  }

  const handleBulkDeliveryChallan = () => {
    const selectedOrders = filteredData.filter(order => selectedProducts.includes(order.id));
    const statusOrder = getStatusOrder();
    
    let minStatusOrder = 999;
    let minStatusCategory = '';
    
    selectedOrders.forEach(order => {
      const orderStatusCategory = (order.status?.category || '').toLowerCase();
      const orderStatusName = order.status?.name || '';
      
      let orderCategory = orderStatusCategory;
      if (!orderCategory && orderStatusName) {
        const orderStatusOption = statusOptions.find(opt => opt.name === orderStatusName);
        orderCategory = orderStatusOption?.category?.toLowerCase() || '';
      }
      
      const orderSequence = statusOrder[orderCategory] || 0;
      if (orderSequence > 0 && orderSequence < minStatusOrder) {
        minStatusOrder = orderSequence;
        minStatusCategory = orderCategory;
      }
    });
    
    let defaultStatus = null;
    
    if (statusFilter === 'Dispatch') {
      defaultStatus = statusOptions.find(status => status.name === 'Delivered') || 
                      statusOptions.find(status => status.name === 'Returned') ||
                      statusOptions.find(status => status.name === 'Cancelled') ||
                      (statusOptions.length > 0 ? statusOptions[0] : null);
    } else if (statusFilter === 'Delivered') {
      // Keep bulk flow aligned with single update: Delivered -> Returned only.
      defaultStatus = statusOptions.find(status => status.name === 'Returned') || null;
    } else if (minStatusOrder !== 999 && minStatusOrder > 0) {
      const nextSequence = minStatusOrder + 1;
      defaultStatus = statusOptions.find(status => {
        const statusCategory = (status.category || '').toLowerCase();
        return statusOrder[statusCategory] === nextSequence;
      });
      
      if (!defaultStatus) {
        defaultStatus = statusOptions.find(status => {
          const statusCategory = (status.category || '').toLowerCase();
          const statusSeq = statusOrder[statusCategory] || 0;
          return statusSeq > minStatusOrder && statusSeq <= minStatusOrder + 1;
        });
      }
      
      if (!defaultStatus) {
        defaultStatus = statusOptions.length > 0 ? statusOptions[0] : null;
      }
    } else {
      defaultStatus = statusOptions.length > 0 ? statusOptions[0] : null;
    }
    
    setBulkChallanModal({
      open: true,
      driverName: '',
      vehicleNo: '',
      deliveryDate: new Date().toISOString().slice(0,10),
      generateInvoice: false,
      isCreating: false,
      error: '',
      emails: [],
      emailInput: '',
      selectedStatus: defaultStatus?.name || '',
      selectedStatusCategory: defaultStatus?.category || '',
      dispatchPersonName: '',
      customDispatchPerson: '',
      useCustomDispatchPerson: false,
      start_km: '',
      end_km: '',
      cancellationReason: '',
      returnReason: ''
    })
  }

  const createBulkDeliveryChallan = async (orderIds, driverName, vehicleNo) => {
    try {
      const formData = new FormData()
      formData.append('order_id', orderIds.join(','))
      formData.append('driver_name', driverName)
      formData.append('vehicle_no', vehicleNo)
      if (bulkChallanModal.deliveryDate) {
        formData.append('delivery_date', bulkChallanModal.deliveryDate)
      }
      formData.append('invoice', bulkChallanModal.generateInvoice ? 'true' : 'false')
      const dispatchPersonName = bulkChallanModal.useCustomDispatchPerson 
        ? bulkChallanModal.customDispatchPerson 
        : bulkChallanModal.dispatchPersonName;
      if (dispatchPersonName) {
        formData.append('dispatch_person_name', dispatchPersonName);
      }

      const response = await apiService.post(
        API_ENDPOINTS.POST_create_delivery_challan,
        formData,
        "multipart/form-data"
      )

      return response
    } catch (error) {
      console.error('Error creating bulk delivery challan:', error)
      throw error
    }
  }

  const handleCloseBulkChallanModal = () => {
    setBulkChallanModal({
      open: false,
      orderBy: '',
      entityName: '',
      driverName: '',
      vehicleNo: '',
      deliveryDate: new Date().toISOString().slice(0,10),
      generateInvoice: false,
      isCreating: false,
      error: '',
      emails: [],
      emailInput: '',
      selectedStatus: '',
      selectedStatusCategory: '',
      dispatchPersonName: '',
      customDispatchPerson: '',
      useCustomDispatchPerson: false,
      start_km: '',
      end_km: '',
      cancellationReason: '',
      returnReason: '',
      bulkPaymentStatus: ''
    })
    setBulkEntityOptions([])
  }

  const handleBulkChallanSubmit = async () => {
    const { driverName, vehicleNo, selectedStatus } = bulkChallanModal

    if (!selectedStatus.trim()) {
      setBulkChallanModal(prev => ({
        ...prev,
        error: 'Please select a status to update orders to'
      }))
      return
    }

    if (selectedProducts.length === 0) {
      setBulkChallanModal(prev => ({
        ...prev,
        error: 'No products selected'
      }))
      return
    }

    if (statusFilter === 'Dispatch' && selectedStatus === 'Dispatch') {
      setBulkChallanModal(prev => ({
        ...prev,
        error: 'Cannot change status to Dispatch when viewing Dispatch tab. You can only change to Delivered, Returned, or Cancelled.'
      }))
      return
    }

    const selectedOrders = filteredData.filter(order => selectedProducts.includes(order.id));
    const statusOrder = getStatusOrder();
    const selectedOption = statusOptions.find(opt => opt.name === selectedStatus);
    const newStatusCategory = (selectedOption?.name || '').toLowerCase();

    const hasUnpaidOrders = selectedOrders.some(order => {
      const paymentStatus = (order.payment_status?.name || order.payment_status || '').toLowerCase();
      return !(paymentStatus === 'paid' || paymentStatus === 'outstanding');
    });

    if (hasUnpaidOrders && selectedStatus !== 'Orders') {
      setBulkChallanModal(prev => ({
        ...prev,
        error: 'Some selected orders must be paid before they can be moved to any status other than "Orders". Please update the payment status first.'
      }));
      return;
    }

    const isReturnedSelected = selectedStatus.toLowerCase() === 'returned';
    const isCancelledSelected = selectedStatus.toLowerCase() === 'cancelled';
    const hasDeliveredOrders = selectedOrders.some(order => {
      const statusName = (order.status?.name || order.status_name || '').toLowerCase();
      const statusCategory = (order.status?.category || '').toLowerCase();
      return statusName === 'delivered' || statusCategory === 'delivered';
    });
    const hasCancelledOrders = selectedOrders.some(order => {
      const statusName = (order.status?.name || order.status_name || '').toLowerCase();
      const statusCategory = (order.status?.category || '').toLowerCase();
      return statusName === 'cancelled' || statusCategory === 'cancelled';
    });

    if (hasCancelledOrders) {
      setBulkChallanModal(prev => ({
        ...prev,
        error: 'Cancelled is a final status. No further status changes are allowed for cancelled orders.'
      }));
      return;
    }

    if (hasDeliveredOrders && !isReturnedSelected) {
      setBulkChallanModal(prev => ({
        ...prev,
        error: 'When an order is Delivered, only Returned status is allowed.'
      }));
      return;
    }

    if (isReturnedSelected && !hasDeliveredOrders) {
      setBulkChallanModal(prev => ({
        ...prev,
        error: 'Returned status is only available when orders are Delivered.'
      }));
      return;
    }

    const alwaysAvailableCategories = ['cancelled', 'returned'];

    if (!alwaysAvailableCategories.includes(newStatusCategory)) {
      let minStatusOrder = 999;
      selectedOrders.forEach(order => {
        const orderStatusCategory = (order.status?.category || '').toLowerCase();
        const orderStatusName = order.status?.name || '';

        let orderCategory = orderStatusCategory;
        if (!orderCategory && orderStatusName) {
          const orderStatusOption = statusOptions.find(opt => opt.name === orderStatusName);
          orderCategory = orderStatusOption?.category?.toLowerCase() || '';
        }

        const orderSequence = statusOrder[orderCategory] || 0;
        if (orderSequence > 0 && orderSequence < minStatusOrder) {
          minStatusOrder = orderSequence;
        }
      });

      if (minStatusOrder === 999) {
        minStatusOrder = 0;
      }
    }

    for (const order of selectedOrders) {
      const originalStatusCategory = (order.status?.category || '').toLowerCase();
      const originalStatusName = (order.status?.name || order.status_name || '').toLowerCase();
      const disabledForOrder = getStatusesToDisable(originalStatusCategory, originalStatusName) || [];
      const newCat = (selectedOption?.category || '').toLowerCase();

      if (disabledForOrder.includes(newCat)) {
        setBulkChallanModal(prev => ({
          ...prev,
          error: 'Selected status is not allowed for one or more of the selected orders due to sequential/final status rules.'
        }));
        return;
      }

      const isDispatch = newCat === 'shipped' || newCat === 'dispatch' || selectedStatus === 'Dispatch';
      if (isDispatch) {
        const processingOrder = statusOrder['processing'] || 0;
        const currentStatusName = (order.status?.name || order.status_name || '').toLowerCase();
        const currentStatusCat = (order.status?.category || '').toLowerCase();
        const currentOrder = statusOrder[currentStatusCat] || 0;

        const isActuallyProcessing = (currentStatusName === 'processing' || currentStatusCat === 'processing') && currentOrder === processingOrder && processingOrder > 0;

        if (!isActuallyProcessing) {
          setBulkChallanModal(prev => ({
            ...prev,
            error: `Dispatch status is only available when orders are in Processing status. One or more selected orders are not in Processing.`
          }));
          return;
        }
      }
    }

    if (selectedStatus === 'Dispatch' && (!driverName.trim() || !vehicleNo.trim())) {
      setBulkChallanModal(prev => ({
        ...prev,
        error: 'Driver name and vehicle number are required for Dispatch status'
      }))
      return
    }

    setBulkChallanModal(prev => ({
      ...prev,
      isCreating: true,
      error: ''
    }))

    try {
      const selectedOrderIds = filteredData
        .filter(product => selectedProducts.includes(product.id))
        .map(product => product.order_id)
        .filter(Boolean)

      if (selectedOrderIds.length === 0) {
        throw new Error('No valid orders found for selected products')
      }

      let response = { success: true };
      
      if (selectedStatus === 'Dispatch') {
        console.log('Creating bulk delivery challan for orders:', selectedOrderIds)
        response = await createBulkDeliveryChallan(selectedOrderIds, driverName, vehicleNo)
      }

      if (response?.success) {
        console.log('Updating order statuses to:', selectedStatus);
        console.log('Order IDs to update:', selectedOrderIds);
        
        try {
          if (selectedStatus === 'Delivered') {
            const startKm = parseFloat(bulkChallanModal.start_km);
            const endKm = parseFloat(bulkChallanModal.end_km);
            
            if (!isNaN(startKm) && !isNaN(endKm) && endKm <= startKm) {
              alert('End KM must be greater than Start KM. Please enter a valid End KM value.');
              setBulkChallanModal(prev => ({
                ...prev,
                error: 'End KM must be greater than Start KM',
                isCreating: false
              }));
              return;
            }
          }

          const userId = localStorage.getItem("user_id");
          const dispatchPersonName = bulkChallanModal.useCustomDispatchPerson 
            ? bulkChallanModal.customDispatchPerson 
            : bulkChallanModal.dispatchPersonName;
          const payload = {
            user_id: userId,
            status: selectedStatus,
            order_id: selectedOrderIds.join(','),
            emails: bulkChallanModal.emails?.length > 0 ? bulkChallanModal.emails.join(',') : '',
            generateInvoice: bulkChallanModal.generateInvoice,
            driverName: selectedStatus === 'Dispatch' ? bulkChallanModal.driverName : '',
            vehicleNo: selectedStatus === 'Dispatch' ? bulkChallanModal.vehicleNo : '',
            delivery_date: bulkChallanModal.deliveryDate || new Date().toISOString().slice(0,10),
            dispatch_person_name: dispatchPersonName,
            start_km: bulkChallanModal.start_km || '',
            ...(selectedStatus === 'Dispatch' && bulkChallanModal.orderBy && { order_by: bulkChallanModal.orderBy }),
            ...(selectedStatus === 'Dispatch' && bulkChallanModal.entityName && { order_by_entity: bulkChallanModal.entityName }),
            ...(selectedStatus === 'Delivered' && { end_km: bulkChallanModal.end_km || '' }),
            ...(selectedStatus === 'Cancelled' && { reason: bulkChallanModal.cancellationReason || '' }),
            ...(selectedStatus === 'Returned' && { reason: bulkChallanModal.returnReason || '' })
          };

          console.log('Bulk status update payload:', payload);

          const statusResponse = await apiService.put(
            API_ENDPOINTS.UPDATE_product_status,
            payload,
            "application/json"
          );
          
          console.log('Bulk status update response:', statusResponse);

          if (statusResponse?.success) {
            setBulkChallanModal({
              open: false,
              driverName: '',
              vehicleNo: '',
              deliveryDate: new Date().toISOString().slice(0,10),
              generateInvoice: false,
              isCreating: false,
              error: '',
              emails: [],
              emailInput: '',
              selectedStatus: '',
              selectedStatusCategory: '',
              dispatchPersonName: '',
              customDispatchPerson: '',
              useCustomDispatchPerson: false,
              start_km: '',
              end_km: '',
              cancellationReason: '',
              returnReason: ''
            })
            
            if (onSelectAll) {
              onSelectAll(false)
            }
            setTimeout(() => {
              onRefresh?.()
            }, 100)
          } else {
            throw new Error(statusResponse?.message || 'Failed to update order statuses')
          }
        } catch (statusError) {
          console.error('Error updating order statuses:', statusError);
          throw new Error(statusError?.message || 'Failed to update order statuses');
        }
      } else {
        throw new Error(response?.message || 'Failed to create bulk delivery challan')
      }
    } catch (error) {
      console.error('Error creating bulk delivery challan:', error)
      setBulkChallanModal(prev => ({
        ...prev,
        error: error.message || 'Failed to create bulk delivery challan',
        isCreating: false
      }))
    }
  }

  const fetchBulkEntitiesByOrderBy = async (orderBy) => {
    if (!orderBy) {
      setBulkEntityOptions([]);
      return;
    }
    
    setLoadingBulkEntities(true);
    try {
      const entityMapping = {
        'indian post': ['Mumbai Branch', 'Delhi Branch', 'Bangalore Branch', 'Chennai Branch'],
        'courier services': ['BlueDart', 'FedEx', 'DTDC', 'First Flight', 'Professional Courier'],
        'name': ['Mumbai Branch', 'Delhi Branch', 'Bangalore Branch', 'Chennai Branch']
      };
      
      const entities = entityMapping[orderBy.toLowerCase()] || [];
      setBulkEntityOptions(entities);
    } catch (error) {
      console.error('Error fetching bulk entities:', error);
      setBulkEntityOptions([]);
    } finally {
      setLoadingBulkEntities(false);
    }
  };

  const handleBulkChallanInputChange = (field, value) => {
    setBulkChallanModal(prev => ({
      ...prev,
      [field]: value,
      error: ''
    }))
    
    if (field === 'orderBy') {
      setBulkChallanModal(prev => ({
        ...prev,
        orderBy: value,
        entityName: '',
        error: ''
      }));
      fetchBulkEntitiesByOrderBy(value);
    }
  }

  const isBulkSubmitDisabled = () => {
    if (bulkChallanModal.isCreating) return true;
    const selectedStatus = bulkChallanModal.selectedStatus;
    if (!selectedStatus || !selectedStatus.trim()) return true;

    const selectedOrders = filteredData.filter(order => selectedProducts.includes(order.id));
    const hasUnpaidOrders = selectedOrders.some(order => {
      const paymentStatus = (order.payment_status?.name || order.payment_status || '').toLowerCase();
      return !(paymentStatus === 'paid' || paymentStatus === 'outstanding');
    });
    if (hasUnpaidOrders && selectedStatus !== 'Orders') return true;

    const selCat = (bulkChallanModal.selectedStatusCategory || '').toLowerCase();
    const anySelectedOrderHasStartKm = selectedOrders.some(order => {
      const startKm = order.delivery_challan?.start_km;
      return startKm !== null && startKm !== undefined && String(startKm).trim() !== '';
    });

    if (selCat === 'shipped' || selectedStatus === 'Dispatch') {
      const dispatchPersonName = bulkChallanModal.useCustomDispatchPerson ? bulkChallanModal.customDispatchPerson : bulkChallanModal.dispatchPersonName;
      if (!bulkChallanModal.orderBy?.trim() || !bulkChallanModal.entityName?.trim() || !bulkChallanModal.driverName?.trim() || !bulkChallanModal.vehicleNo?.trim() || !dispatchPersonName?.trim()) return true;
    }

    if (selectedStatus === 'Delivered' && anySelectedOrderHasStartKm) {
      const startKm = parseFloat(bulkChallanModal.start_km);
      const endKm = parseFloat(bulkChallanModal.end_km);
      if (!String(bulkChallanModal.end_km || '').trim()) return true;
      if (!isNaN(startKm) && !isNaN(endKm) && endKm <= startKm) return true;
    }

    if (selectedStatus === 'Cancelled' && !bulkChallanModal.cancellationReason?.trim()) return true;
    if (selectedStatus === 'Returned' && !bulkChallanModal.returnReason?.trim()) return true;

    return false;
  }

  const addEmailToBulkModal = () => {
    const email = bulkChallanModal.emailInput?.trim()
    if (email && !bulkChallanModal.emails?.includes(email)) {
      setBulkChallanModal(prev => ({
        ...prev,
        emails: [...(prev.emails || []), email],
        emailInput: ''
      }))
    }
  }

  const removeEmailFromBulkModal = (emailToRemove) => {
    setBulkChallanModal(prev => ({
      ...prev,
      emails: prev.emails?.filter(email => email !== emailToRemove) || []
    }))
  }

  const getEligibleProductsCount = () => {
    return filteredData
      .filter(order => selectedProducts.includes(order.id))
      .filter(order => !extractDeliveryChallan(order)?.path)
      .length
  }

  const getSelectedProductsCount = () => {
    return filteredData
      .filter(order => selectedProducts.includes(order.id))
      .length
  }

  const handleProductSelection = (orderId, isSelected) => {
    if (onProductSelect) {
      onProductSelect(orderId, isSelected)
    }
  }

  const handleSelectAllToggle = () => {
    if (onSelectAll) {
      onSelectAll(!isAllSelected)
    }
  }

  const isProductSelected = (orderId) => {
    return selectedProducts.includes(orderId)
  }

  const toggleRow = (orderId) => {
    const newExpanded = new Set(expandedRows)
    if (newExpanded.has(orderId)) {
      newExpanded.delete(orderId)
    } else {
      newExpanded.add(orderId)
    }
    setExpandedRows(newExpanded)
  }

  const createDeliveryChallan = async (orderId, driverName, vehicleNo) => {
    try {
      const formData = new FormData();
      formData.append('order_id', orderId);
      formData.append('driver_name', driverName);
      formData.append('vehicle_no', vehicleNo);
      if (statusUpdateModal.shippedDate) {
        formData.append('delivery_date', statusUpdateModal.shippedDate);
      }
      formData.append('invoice', statusUpdateModal.generateInvoice ? 'true' : 'false');
      const dispatchPersonName = statusUpdateModal.useCustomDispatchPerson 
        ? statusUpdateModal.customDispatchPerson 
        : statusUpdateModal.dispatchPersonName;
      if (dispatchPersonName) {
        formData.append('dispatch_person_name', dispatchPersonName);
      }
      if (statusUpdateModal.orderBy) {
        formData.append('order_by', statusUpdateModal.orderBy);
      }
      if (statusUpdateModal.entityName) {
        formData.append('order_by_entity', statusUpdateModal.entityName);
      }

      const response = await apiService.post(
        API_ENDPOINTS.POST_create_delivery_challan,
        formData,
        "multipart/form-data"
      );

      return response;
    } catch (error) {
      console.error('Error creating delivery challan:', error);
      throw error;
    }
  };

  const getStatusOrder = () => {
    if (!statusOptions || statusOptions.length === 0) {
      return {};
    }
    
    const orderMap = {};
    statusOptions.forEach(option => {
      const category = (option.category || '').toLowerCase();
      const sequence = option.sequence || 0;
      if (category) {
        orderMap[category] = sequence;
      }
    });
    
    return orderMap;
  };

  const getNextSequentialStatus = (currentStatusName, currentStatusCategory) => {
    if (!statusOptions || statusOptions.length === 0) {
      return null;
    }

    const statusOrder = getStatusOrder();
    let currentStatusLower = (currentStatusCategory || '').toLowerCase();
    let currentOrder = statusOrder[currentStatusLower] || 0;

    if (currentOrder === 0 && currentStatusName) {
      const currentStatusOption = statusOptions.find(opt => opt.name === currentStatusName);
      if (currentStatusOption) {
        currentStatusLower = (currentStatusOption.category || '').toLowerCase();
        currentOrder = statusOrder[currentStatusLower] || 0;
      }
    }

    if (currentOrder === 0) {
      return null;
    }
    
    const isDelivered = (currentStatusName || '').toLowerCase() === 'delivered' ||
                       (currentStatusCategory || '').toLowerCase() === 'delivered';
    
    if (isDelivered) {
      const returnedStatus = statusOptions.find(option => {
        const optionCategory = (option.category || '').toLowerCase();
        const optionName = (option.name || '').toLowerCase();
        return optionCategory === 'returned' || optionName === 'returned';
      });
      return returnedStatus || null;
    }
    
    const nextOrder = currentOrder + 1;
    const nextStatus = statusOptions.find(option => {
      const optionCategory = (option.category || '').toLowerCase();
      return statusOrder[optionCategory] === nextOrder;
    });

    return nextStatus || null;
  };

  const getStatusesToDisable = (currentStatusCategory, currentStatusName) => {
    if (!statusOptions || statusOptions.length === 0) {
      return [];
    }
    
    const statusOrder = getStatusOrder();
    let currentStatusLower = (currentStatusCategory || '').toLowerCase();
    let currentOrder = statusOrder[currentStatusLower] || 0;
    
    if (currentOrder === 0 && currentStatusName) {
      const currentStatusOption = statusOptions.find(opt => opt.name === currentStatusName);
      if (currentStatusOption) {
        currentStatusLower = (currentStatusOption.category || '').toLowerCase();
        currentOrder = statusOrder[currentStatusLower] || 0;
      }
    }
    
    const dispatchOrder = statusOrder['shipped'] || statusOrder['dispatch'] || 999;
    const processingOrder = statusOrder['processing'] || 0;
    const deliveredOrder = statusOrder['delivered'] || 999;
    
    const isProcessingStatus = currentStatusName?.toLowerCase() === 'processing' || 
                                currentStatusLower === 'processing' ||
                                currentOrder === processingOrder;
    
    const isDeliveredStatus = currentStatusName?.toLowerCase() === 'delivered' || 
                              currentStatusLower === 'delivered' ||
                              currentOrder === deliveredOrder;
    
    const isCancelledStatus = currentStatusName?.toLowerCase() === 'cancelled' || 
                              currentStatusLower === 'cancelled' ||
                              currentStatusLower === 'cancelled';
    
    if (currentOrder === 0) {
      return statusOptions
        .filter(option => {
          const optionCategory = (option.category || '').toLowerCase();
          const optionName = (option.name || '').toLowerCase();
          if (optionCategory === 'shipped' || optionCategory === 'dispatch' || optionName === 'dispatch') {
            return true;
          }
          return true;
        })
        .map(option => option.category?.toLowerCase() || '');
    }
    
    if (isCancelledStatus) {
      return statusOptions.filter(option => {
        const optionCategory = (option.category || '').toLowerCase();
        const optionName = (option.name || '').toLowerCase();
        const isReturned = optionCategory === 'returned' || optionName === 'returned';
        return !isReturned;
      }).map(option => option.category?.toLowerCase() || '');
    }
    
    if (isDeliveredStatus) {
      return statusOptions
        .filter(option => {
          const optionCategory = (option.category || '').toLowerCase();
          const optionName = (option.name || '').toLowerCase();
          const isReturned = optionCategory === 'returned' || optionName === 'returned';
          return !isReturned;
        })
        .map(option => option.category?.toLowerCase() || '');
    }
    
    return statusOptions.filter(option => {
      const optionCategory = (option.category || '').toLowerCase();
      const optionName = (option.name || '').toLowerCase();
      const isReturned = optionCategory === 'returned' || optionName === 'returned';
      const isCancelled = optionCategory === 'cancelled' || optionName === 'cancelled';
      const isDispatch = optionCategory === 'shipped' || optionCategory === 'dispatch' || optionName === 'dispatch';
      
      if (isDispatch) {
        return !isProcessingStatus;
      }
      
      if (isReturned) {
        return !isDeliveredStatus;
      }
      
      const statusOrderValue = statusOrder[optionCategory] || 0;
      const dispatchOrder = statusOrder['shipped'] || statusOrder['dispatch'] || 999;
      
      if (statusOrderValue < currentOrder) {
        return true;
      }
      
      if (statusOrderValue === currentOrder) {
        return true;
      }
      
      if (isCancelled) {
        if (currentOrder <= dispatchOrder) {
          return false;
        } else {
          return true;
        }
      }
      
      if (statusOrderValue > currentOrder + 1) {
        return true;
      }
      
      return false;
    }).map(option => option.category?.toLowerCase() || '');
  };

  const isSingleStatusSubmitDisabled = () => {
    if (updatingStatus || statusUpdateModal.isCreatingChallan) return true;

    const selectedStatus = statusUpdateModal.order?.status?.name || statusUpdateModal.order?.status_name;
    if (!selectedStatus) return true;

    const paymentStatus = (statusUpdateModal.order?.payment_status?.name || statusUpdateModal.order?.payment_status || '').toLowerCase();
    const isPaid = paymentStatus === 'paid' || paymentStatus === 'outstanding';
    const isOrdersStatus = selectedStatus === 'Orders';

    const originalStatusName = (statusUpdateModal.originalStatus?.name || '').toLowerCase();
    const originalStatusCategory = (statusUpdateModal.originalStatus?.category || '').toLowerCase();
    const isOriginallyDelivered = originalStatusName === 'delivered' || originalStatusCategory === 'delivered';
    const isReturnedSelected = selectedStatus === 'Returned';

    const isShippedCategory = (statusUpdateModal.order?.status?.category || '').toLowerCase() === 'shipped';
    if (
      isShippedCategory &&
      (
        !statusUpdateModal.orderBy?.trim() ||
        !statusUpdateModal.entityName?.trim() ||
        !statusUpdateModal.driverName.trim() ||
        !statusUpdateModal.vehicleNo.trim()
      )
    ) {
      return true;
    }

    if (selectedStatus === 'Delivered') {
      const startKmFromModal = statusUpdateModal.start_km;
      const startKmFromOrder = statusUpdateModal.order?.delivery_challan?.start_km;
      const startKm = parseFloat(startKmFromModal || startKmFromOrder);
      const endKm = parseFloat(statusUpdateModal.end_km);
      if (!isNaN(startKm) && !isNaN(endKm) && endKm <= startKm) return true;
    }

    if (selectedStatus === 'Cancelled' && !statusUpdateModal.cancellationReason?.trim()) return true;
    if (selectedStatus === 'Returned' && !statusUpdateModal.returnReason?.trim()) return true;

    if (
      selectedStatus !== 'Returned' &&
      selectedStatus !== 'Cancelled' &&
      !isPaid &&
      !isOrdersStatus &&
      !(isOriginallyDelivered && isReturnedSelected)
    ) {
      return true;
    }

    return false;
  };

  const handleStatusUpdate = async (id, statusName, orderId) => {
    try {
      setUpdatingStatus(true);
      
      const originalStatusCategory = (statusUpdateModal.originalStatus?.category || '').toLowerCase();
      const originalStatusName = (statusUpdateModal.originalStatus?.name || '').toLowerCase();
      const currentStatusCategory = originalStatusCategory || (statusUpdateModal.order?.status?.category || statusUpdateModal.order?.status_name || '').toLowerCase();
      const currentStatusName = originalStatusName || (statusUpdateModal.order?.status?.name || statusUpdateModal.order?.status_name || '').toLowerCase();
      const selectedOption = statusOptions.find(opt => opt.name === statusName);
      const newStatusCategory = (selectedOption?.category || '').toLowerCase();
      const newStatusName = (statusName || '').toLowerCase();
      
      const statusOrder = getStatusOrder();
      const currentOrder = statusOrder[currentStatusCategory] || 0;
      const newOrder = statusOrder[newStatusCategory] || 0;
      const processingOrder = statusOrder['processing'] || 0;
      const deliveredOrder = statusOrder['delivered'] || 999;
      
      const isProcessingStatus = currentStatusName === 'processing' || 
                                  currentStatusCategory === 'processing' ||
                                  currentOrder === processingOrder;
      
      const isDeliveredStatus = currentStatusName === 'delivered' || 
                                currentStatusCategory === 'delivered' ||
                                currentOrder === deliveredOrder;
      
      const isCancelledStatus = currentStatusName === 'cancelled' || 
                                currentStatusCategory === 'cancelled';
      
      const isCancelledOrReturned = newStatusCategory === 'cancelled' || newStatusCategory === 'returned' ||
                                    newStatusName === 'cancelled' || newStatusName === 'returned';
      const isReturned = newStatusCategory === 'returned' || newStatusName === 'returned';
      const isDispatch = newStatusCategory === 'shipped' || newStatusCategory === 'dispatch' || newStatusName === 'dispatch';
      
      const paymentStatus = (statusUpdateModal.order?.payment_status?.name || statusUpdateModal.order?.payment_status || '').toLowerCase();
      const isPaid = paymentStatus === 'paid' || paymentStatus === 'outstanding';
      const isOrdersStatus = statusName === 'Orders';
      const isOriginallyDelivered = originalStatusName === 'delivered' || originalStatusCategory === 'delivered';
      
      if (!isPaid && !isOrdersStatus) {
        if (isReturned && !isOriginallyDelivered) {
          setStatusUpdateModal(prev => ({
            ...prev,
            challanError: 'Returned status is only available when the order is Delivered.'
          }));
          setUpdatingStatus(false);
          return;
        }

        if (!(isOriginallyDelivered && isReturned)) {
          setStatusUpdateModal(prev => ({
            ...prev,
            challanError: 'This order must be paid before it can be moved to any status other than "Orders". Please update the payment status first.'
          }));
          setUpdatingStatus(false);
          return;
        }
      }
      
      if (isDeliveredStatus) {
        if (!isReturned) {
          setStatusUpdateModal(prev => ({
            ...prev,
            challanError: `When an order is Delivered, only Returned status is allowed.`
          }));
          setUpdatingStatus(false);
          return;
        }
      } else {
        if (isReturned) {
          setStatusUpdateModal(prev => ({
            ...prev,
            challanError: `Returned status is only available when the order is Delivered.`
          }));
          setUpdatingStatus(false);
          return;
        }
        
        if (isCancelledStatus) {
          setStatusUpdateModal(prev => ({
            ...prev,
            challanError: `Cancelled is a final status. No further status changes are allowed.`
          }));
          setUpdatingStatus(false);
          return;
        }
        
        if (isDispatch) {
          const dispatchOrder = statusOrder['shipped'] || statusOrder['dispatch'] || 999;
          
          const isActuallyProcessing = (currentStatusName === 'processing' || 
                                      currentStatusCategory === 'processing') &&
                                      currentOrder === processingOrder &&
                                      processingOrder > 0;
          
          if (!isActuallyProcessing) {
            const currentStatusDisplay = statusUpdateModal.originalStatus?.name || 
                                       statusUpdateModal.order?.status?.name || 
                                       statusUpdateModal.order?.status_name || 
                                       currentStatusCategory ||
                                       'Unknown';
            setStatusUpdateModal(prev => ({
              ...prev,
              challanError: `Dispatch status is only available when the order is in Processing status. Current status: ${currentStatusDisplay}.`
            }));
            setUpdatingStatus(false);
            return;
          }
        }
      }
      
      if (!isCancelledOrReturned) {
        if (newOrder > currentOrder + 1) {
          setStatusUpdateModal(prev => ({
            ...prev,
            challanError: `Status must be updated sequentially. You can only move to the next status in the sequence. Current: ${statusUpdateModal.order?.status?.name || currentStatusCategory}, Next allowed: ${statusOptions.find(opt => {
              const optOrder = statusOrder[(opt.category || '').toLowerCase()] || 0;
              return optOrder === currentOrder + 1;
            })?.name || 'N/A'}.`
          }));
          setUpdatingStatus(false);
          return;
        }
        
        if (newOrder < currentOrder) {
          setStatusUpdateModal(prev => ({
            ...prev,
            challanError: `Cannot move to a previous status. Current status: ${statusUpdateModal.order?.status?.name || currentStatusCategory}.`
          }));
          setUpdatingStatus(false);
          return;
        }
      }
      
      const selectedOptionForShipping = statusOptions.find(opt => opt.name === statusName);
      const statusCategoryForShipping = (selectedOptionForShipping?.category || '').toLowerCase();
      if (statusCategoryForShipping === 'shipped') {
        const { driverName, vehicleNo } = statusUpdateModal;
        
        if (!statusUpdateModal.orderBy?.trim() || !statusUpdateModal.entityName?.trim() || !driverName.trim() || !vehicleNo.trim() || !statusUpdateModal.shippedDate) {
          setStatusUpdateModal(prev => ({
            ...prev,
            challanError: 'Order By, Entity Name, Driver name, vehicle number, and Delivery Date are required for shipping'
          }));
          setUpdatingStatus(false);
          return;
        }
        
        const dispatchPersonName = statusUpdateModal.useCustomDispatchPerson 
          ? statusUpdateModal.customDispatchPerson 
          : statusUpdateModal.dispatchPersonName;
        if (!dispatchPersonName?.trim()) {
          setStatusUpdateModal(prev => ({
            ...prev,
            challanError: 'Dispatch Person Name is required. Please select a dispatch person or add a custom one.'
          }));
          setUpdatingStatus(false);
          return;
        }

        setStatusUpdateModal(prev => ({
          ...prev,
          isCreatingChallan: true,
          challanError: ''
        }));

        try {
          const challanResponse = await createDeliveryChallan(orderId, driverName, vehicleNo);
          
          if (!challanResponse?.success) {
            throw new Error(challanResponse?.message || 'Failed to create delivery challan');
          }

          console.log('Delivery challan created successfully');
        } catch (challanError) {
          console.error('Error creating delivery challan:', challanError);
          setStatusUpdateModal(prev => ({
            ...prev,
            challanError: challanError.message || 'Failed to create delivery challan',
            isCreatingChallan: false
          }));
          setUpdatingStatus(false);
          return;
        }

        setStatusUpdateModal(prev => ({
          ...prev,
          isCreatingChallan: false
        }));
      }

      if (statusName === 'Delivered') {
        const startKmFromModal = statusUpdateModal.start_km;
        const startKmFromOrder = statusUpdateModal.order?.delivery_challan?.start_km;
        const startKm = parseFloat(startKmFromModal || startKmFromOrder);
        const endKm = parseFloat(statusUpdateModal.end_km);
        
        if (!isNaN(startKm) && !isNaN(endKm) && endKm <= startKm) {
          alert('End KM must be greater than Start KM. Please enter a valid End KM value.');
          setStatusUpdateModal(prev => ({
            ...prev,
            challanError: 'End KM must be greater than Start KM'
          }));
          setUpdatingStatus(false);
          return;
        }
      }

      const userId = localStorage.getItem("user_id");
      const dispatchPersonName = statusUpdateModal.useCustomDispatchPerson 
        ? statusUpdateModal.customDispatchPerson 
        : statusUpdateModal.dispatchPersonName;
      const payload = {
        user_id: userId,
        status: statusName,
        order_id: orderId,
        emails: statusUpdateModal.emails,
        generateInvoice: statusUpdateModal.generateInvoice,
        driverName: statusUpdateModal.driverName,
        vehicleNo: statusUpdateModal.vehicleNo,
        delivery_date: statusUpdateModal.shippedDate,
        dispatch_person_name: dispatchPersonName,
        start_km: statusUpdateModal.start_km,
        ...(statusUpdateModal.orderBy && { order_by: statusUpdateModal.orderBy }),
        ...(statusUpdateModal.entityName && { order_by_entity: statusUpdateModal.entityName }),
        ...(statusName === 'Delivered' && { end_km: statusUpdateModal.end_km }),
        ...(statusName === 'Cancelled' && { reason: statusUpdateModal.cancellationReason || '' }),
        ...(statusName === 'Returned' && { reason: statusUpdateModal.returnReason || '' })
      };

      const response = await apiService.put(
        API_ENDPOINTS.UPDATE_product_status,
        payload,
        "application/json"
      );

      if (response?.success) {
        setStatusUpdateModal({ 
          open: false, 
          order: null,
          driverName: '',
          vehicleNo: '',
          delivery_date: new Date().toISOString().slice(0,10),
          generateInvoice: false,
          isCreatingChallan: false,
          challanError: '',
          dispatchPersonName: '',
          customDispatchPerson: '',
          useCustomDispatchPerson: false,
          start_km: '',
          end_km: '',
          cancellationReason: '',
          returnReason: ''
        });
        
        if (onSelectAll) {
          onSelectAll(false);
        }
        setTimeout(() => {
          onRefresh?.();
        }, 100);
      } else {
        console.error('Status update failed:', response?.message);
        setStatusUpdateModal(prev => ({
          ...prev,
          challanError: response?.message || 'Failed to update status'
        }));
      }
    } catch (error) {
      console.error('Error updating status:', error);
      setStatusUpdateModal(prev => ({
        ...prev,
        challanError: 'An error occurred while updating status'
      }));
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handlePaymentStatusUpdate = async (orderId, newStatus, order_id, refundAmount, orderTotalAmount) => {
    setUpdatingPayment(true)
    try {
      const userId = localStorage.getItem("user_id");
      const isRefunded = String(newStatus || '').toLowerCase() === 'refunded';
      const refundVal = (refundAmount != null && refundAmount !== '') ? refundAmount : (orderTotalAmount ?? 0);
      const payload = {
        order_id: order_id,
        payment_status: newStatus,
        user_id: userId,
        ...(isRefunded && { refund_amount: refundVal }),
      };
      const response = await apiService.put(
        `${API_ENDPOINTS.UPDATE_payment_status}`,
        payload
      )
      if (response?.success) {
        await onRefresh()
        setPaymentUpdateModal({ open: false, order: null, refundAmount: '' })
      }
    } catch (error) {
      console.error("Error updating payment status:", error)
    } finally {
      setUpdatingPayment(false)
    }
  }

  const handleBulkPaymentStatusUpdate = async () => {
    if (!bulkChallanModal.bulkPaymentStatus) {
      setBulkChallanModal(prev => ({
        ...prev,
        error: 'Please select a payment status'
      }))
      return
    }

    setIsUpdatingBulkPayment(true)
    try {
      const userId = localStorage.getItem("user_id");
      const selectedOrders = filteredData.filter(order => selectedProducts.includes(order.id));
      
      const unpaidOrders = selectedOrders.filter(order => {
        const paymentStatus = (order.payment_status?.name || order.payment_status || '').toLowerCase();
        const isPaid = paymentStatus === 'paid' || paymentStatus === 'outstanding';
        return !isPaid;
      });

      if (unpaidOrders.length === 0) {
        setBulkChallanModal(prev => ({
          ...prev,
          error: 'All selected orders are already paid or outstanding. No payment status update needed.'
        }))
        setIsUpdatingBulkPayment(false)
        return
      }

      const unpaidOrderIds = unpaidOrders.map(order => order.order_id).filter(Boolean);

      const updatePromises = unpaidOrderIds.map(orderId => {
        const payload = {
          order_id: orderId,
          payment_status: bulkChallanModal.bulkPaymentStatus,
          user_id: userId,
        };
        return apiService.put(
          `${API_ENDPOINTS.UPDATE_payment_status}`,
          payload
        );
      });

      const responses = await Promise.all(updatePromises);
      const allSuccess = responses.every(response => response?.success);

      if (allSuccess) {
        setBulkChallanModal(prev => ({
          ...prev,
          bulkPaymentStatus: '',
          error: ''
        }))
        await onRefresh()
      } else {
        setBulkChallanModal(prev => ({
          ...prev,
          error: 'Some payment status updates failed. Please try again.'
        }))
      }
    } catch (error) {
      console.error("Error updating bulk payment status:", error)
      setBulkChallanModal(prev => ({
        ...prev,
        error: 'Error updating payment status. Please try again.'
      }))
    } finally {
      setIsUpdatingBulkPayment(false)
    }
  }

  const handleBulkPaymentStatusUpdateFromModal = async () => {
    if (!bulkPaymentStatusModal.selectedPaymentStatus) {
      setBulkPaymentStatusModal(prev => ({
        ...prev,
        error: 'Please select a payment status'
      }))
      return
    }

    setBulkPaymentStatusModal(prev => ({ ...prev, isUpdating: true, error: '' }))
    try {
      const userId = localStorage.getItem("user_id");
      const selectedOrders = filteredData.filter(order => selectedProducts.includes(order.id));
      const selectedOrderIds = selectedOrders.map(order => order.order_id).filter(Boolean);

      if (selectedOrderIds.length === 0) {
        setBulkPaymentStatusModal(prev => ({
          ...prev,
          error: 'No orders selected',
          isUpdating: false
        }))
        return
      }

      const updatePromises = selectedOrderIds.map(orderId => {
        const payload = {
          order_id: orderId,
          payment_status: bulkPaymentStatusModal.selectedPaymentStatus,
          user_id: userId,
        };
        return apiService.put(
          `${API_ENDPOINTS.UPDATE_payment_status}`,
          payload
        );
      });

      const responses = await Promise.all(updatePromises);
      const allSuccess = responses.every(response => response?.success);

      if (allSuccess) {
        setBulkPaymentStatusModal({
          open: false,
          selectedPaymentStatus: '',
          error: '',
          isUpdating: false
        })
        await onRefresh()
      } else {
        setBulkPaymentStatusModal(prev => ({
          ...prev,
          error: 'Some payment status updates failed. Please try again.',
          isUpdating: false
        }))
      }
    } catch (error) {
      console.error("Error updating bulk payment status:", error)
      setBulkPaymentStatusModal(prev => ({
        ...prev,
        error: 'Error updating payment status. Please try again.',
        isUpdating: false
      }))
    }
  }

  const handleOpenBulkPaymentStatusModal = () => {
    setBulkPaymentStatusModal({
      open: true,
      selectedPaymentStatus: '',
      error: '',
      isUpdating: false
    })
    closeActionsMenu()
  }

  const handleCloseBulkPaymentStatusModal = () => {
    setBulkPaymentStatusModal({
      open: false,
      selectedPaymentStatus: '',
      error: '',
      isUpdating: false
    })
  }

  const handleOpenStatusModal = (order) => {
    const originalStatus = {
      name: order?.status?.name || order?.status_name || '',
      category: order?.status?.category || ''
    };
    const isCancelledStatus = (originalStatus.name || '').toLowerCase() === 'cancelled' || 
                              (originalStatus.category || '').toLowerCase() === 'cancelled';
    
    let nextStatus = null;
    if (isCancelledStatus) {
      nextStatus = statusOptions.find(opt => 
        (opt.name || '').toLowerCase() === 'returned' || 
        (opt.category || '').toLowerCase() === 'returned'
      );
    } else {
      nextStatus = getNextSequentialStatus(originalStatus.name, originalStatus.category);
    }
    
    const updatedOrder = nextStatus ? {
      ...order,
      status: {
        name: nextStatus.name,
        category: nextStatus.category
      },
      status_name: nextStatus.name
    } : order;
    
    setStatusUpdateModal({
      open: true,
      order: updatedOrder,
      originalStatus: originalStatus,
      entityName: '',
      orderBy: '',
      driverName: '',
      vehicleNo: '',
      generateInvoice: false,
      isCreatingChallan: false,
      challanError: ''
    });
    setEntityOptions([]);
  };

  const handleDownload = async (type, downloadFunction) => {
    setIsDownloading(true);
    closeActionsMenu();
    
    try {
      await downloadFunction(type);
    } catch (error) {
      console.error('Download failed:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleCloseStatusModal = () => {
    setStatusUpdateModal({
      open: false,
      order: null,
      entityName: '',
      orderBy: '',
      driverName: '',
      vehicleNo: '',
      delivery_date: new Date().toISOString().slice(0,10),
      generateInvoice: false,
      isCreatingChallan: false,
      challanError: '',
      dispatchPersonName: '',
      customDispatchPerson: '',
      useCustomDispatchPerson: false,
      start_km: '',
      end_km: '',
      cancellationReason: '',
      returnReason: ''
    });
  };

  const handleStatusModalChange = (field, value) => {
    setStatusUpdateModal(prev => ({
      ...prev,
      [field]: value,
      challanError: ''
    }));
  };

  const filteredData = products.filter((order) => {
    const matchesSearch = (order.user?.email || order.user_email || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
                          (order.order_id || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
                          (order.user?.full_name || order.user_name || "").toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount).toLocaleString('en-IN')}`;
  };

  const getStatusIcon = (status) => {
    const category = typeof status === 'object' && status !== null && 'category' in status
      ? status.category
      : status
    return getIconForCategory(category)
  };

  const OrderDetailsRow = ({ order }) => (
    <ExpandableRow>
      <StyledTableCell colSpan={7} sx={{ paddingY: 0, paddingX: 0 }}>
        <Collapse in={expandedRows.has(order.id)} timeout="auto" unmountOnExit>
          <Box sx={{ width: '100%', padding: 2 }}>
            <Grid container spacing={2.5} alignItems="stretch">
              <Grid item xs={12} md={4} sx={{ minWidth: 0 }}>
                <DetailCard elevation={2} sx={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: 'background.paper', border: `1px solid ${alpha(theme.palette.divider, 0.5)}`, borderRadius: 3, p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Person sx={{ mr: 1, color: theme.palette.primary.main }} />
                    <Typography variant="h6" fontWeight={600}>Customer Information</Typography>
                  </Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Name:</strong> {order.user?.full_name || order.user_name}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Order ID:</strong> {order.order_id || "N/A"}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Email:</strong> {order.user?.email || order.user_email}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Order Date:</strong> {order.order_date_formatted || "N/A"}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Mobile:</strong> {order.user?.mobile_number || order.mobile_number}
                  </Typography>
                  
                  {(order.farmer_name || order.advance_payment?.order?.farmer_name) && (
                    <Box sx={{ mt: 1, mb: 1, p: 1.5, backgroundColor: alpha(theme.palette.info.main, 0.1), borderRadius: 1, border: `1px solid ${alpha(theme.palette.info.main, 0.3)}` }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: "grey.600", mb: 0.5 }}>
                        Order on Behalf of : {order.farmer_name || order.advance_payment?.order?.farmer_name}
                      </Typography>
                    </Box>
                  )}
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 1 }}>
                    <LocationOn sx={{ mr: 1, color: theme.palette.secondary.main }} />
                    <Typography variant="subtitle2" fontWeight={600}>Delivery Address</Typography>
                  </Box>
                  <Tooltip title={order.delivery_address || (order.address_details ? `${order.address_details.full_address}, ${order.address_details.village}, ${order.address_details.tehsil}, ${order.address_details.pincode}` : 'Address not available')} arrow placement="top">
                    <Typography variant="body2" color="text.secondary" sx={{ backgroundColor: alpha(theme.palette.background.default, 0.5), padding: 1.5, borderRadius: 1, border: `1px solid ${alpha(theme.palette.divider, 0.1)}`, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'help' }}>
                      {order.delivery_address || (order.address_details ? `${order.address_details.full_address}, ${order.address_details.village}, ${order.address_details.tehsil}, ${order.address_details.pincode}` : 'Address not available')}
                    </Typography>
                  </Tooltip>
                  
                  {order.notes && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }} fontWeight={600}>Special Instructions:</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', p: 1.5, backgroundColor: alpha(theme.palette.info.main, 0.1), borderRadius: 1, border: `1px solid ${alpha(theme.palette.info.main, 0.2)}` }}>
                        {order.notes}
                      </Typography>
                    </Box>
                  )}
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 1 }}>
                    <LocationOn sx={{ mr: 1, color: theme.palette.warning.main }} />
                    <Typography variant="subtitle2" fontWeight={600}>Billing Address</Typography>
                  </Box>
                  <Tooltip title={order.billing_address || (order.bill_address_details ? `${order.bill_address_details.full_address}, ${order.bill_address_details.village}, ${order.bill_address_details.tehsil}, ${order.bill_address_details.pincode}` : 'Billing address not available')} arrow placement="top">
                    <Typography variant="body2" color="text.secondary" sx={{ backgroundColor: alpha(theme.palette.background.default, 0.5), padding: 1.5, borderRadius: 1, border: `1px solid ${alpha(theme.palette.divider, 0.1)}`, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'help' }}>
                      {order.billing_address || (order.bill_address_details ? `${order.bill_address_details.full_address}, ${order.bill_address_details.village}, ${order.bill_address_details.tehsil}, ${order.bill_address_details.pincode}` : 'Billing address not available')}
                    </Typography>
                  </Tooltip>
                </DetailCard>
              </Grid>

              <Grid item xs={12} md={4} sx={{ minWidth: 0 }}>
                <DetailCard elevation={2} sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 2.5, backgroundColor: 'background.paper', border: `1px solid ${alpha(theme.palette.divider, 0.5)}`, borderRadius: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
                    <LocalShipping sx={{ mr: 1.25, fontSize: '1.25rem', color: theme.palette.success.main }} />
                    <Typography variant="h6" fontWeight={600}>Order Status</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        <strong>Order Status:</strong> <StatusChip label={order.status?.name || order.order_status_display} status={order.status?.category?.toLowerCase() || order.status?.name?.toLowerCase()} size="small" icon={getStatusIcon(order.status)} />
                      </Typography>
                    </Box>
                  </Box>
                  
                  {order.expected_delivery_date && (
                    <Box sx={{ mb: 1.5 }}>
                      <Typography variant="body2"><strong>Expected Delivery Date (Farmer):</strong></Typography>
                      <Typography variant="body2" color="primary" fontWeight={600} sx={{ mt: 0.5 }}>
                        {new Date(order.expected_delivery_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                      </Typography>
                    </Box>
                  )}
                  
                  {order.delivery_date && (
                    <Box sx={{ mt: 2.5, mb: 1.5 }}>
                      <Typography variant="body2" sx={{ mb: 1.25 }}>
                        <strong>Actual Delivery Date:</strong>{' '}
                        <Typography component="span" variant="body2" color="success.main" fontWeight={600}>
                          {(() => {
                            try {
                              const date = new Date(order.delivery_date);
                              return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
                            } catch (e) {
                              return order.delivery_date_formatted || order.delivery_date;
                            }
                          })()}
                        </Typography>
                      </Typography>
                    </Box>
                  )}

                  {(order.status?.category || order.status?.name || '').toString().toLowerCase() === 'returned' && (
                    <Box sx={{ mt: 2.5, mb: 1.5 }}>
                      <Typography variant="body2" sx={{ mb: 1.25 }}>
                        <strong>Returned At:</strong>{' '}
                        {(() => {
                          const dt = order.advance_payment.order.returned_at ?? order.status?.returned_at;
                          if (!dt) return 'N/A';
                          try {
                            const date = new Date(dt);
                            return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
                          } catch (e) {
                            return String(dt);
                          }
                        })()}
                      </Typography>
                    </Box>
                  )}

                  {(order.status?.category || order.status?.name || '').toString().toLowerCase() === 'cancelled' && (
                    <Box sx={{ mt: 2.5, mb: 1.5 }}>
                      <Typography variant="body2" sx={{ mb: 1.25 }}>
                        <strong>Cancelled At:</strong>{' '}
                        {(() => {
                          const dt = order.advance_payment.order.cancelled_at ?? order.status?.cancelled_at;
                          if (!dt) return 'N/A';
                          try {
                            const date = new Date(dt); 
                            return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
                          } catch (e) {
                            return String(dt);
                          }
                        })()}
                      </Typography>
                    </Box>
                  )}
                  
                  {(order.reason ?? order?.advance_payment?.order?.reason) && (
                    <Box sx={{ mb: 2.5 }}>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>Reason:</strong> {order.reason ?? order?.advance_payment?.order?.reason ?? "—"}
                      </Typography>
                    </Box>
                  )}

                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Payment sx={{ mr: 1.25, fontSize: '1.25rem', color: theme.palette.info.main }} />
                    <Typography variant="subtitle1" fontWeight={600}>Payment Information</Typography>
                  </Box>
                  <Typography variant="body2" sx={{ mb: 1.25 }}>
                    <strong>Payment Status:</strong> 
                    <PaymentStatusChip label={order.payment_status?.name || order.payment_status_display || 'Pending'} status={(order.payment_status?.name || order.payment_status || 'pending')?.toLowerCase()} size="small" sx={{ ml: 1.25 }} />
                  </Typography>
                  
                  {((order.reason ?? order.status?.reason) != null && String(order.reason ?? order.status?.reason ?? '').trim() !== '') && (
                    <Typography variant="body2" sx={{ mb: 1.25, mt: 1 }}>
                      <strong>Reason:</strong> {order.reason ?? order.status?.reason ?? ''}
                    </Typography>
                  )}
                  
                  {order?.latest_refund && (
                    <>
                      <Typography variant="body2" sx={{ mb: 1.25 }}>
                        <strong>Refunded Amount:</strong> ₹{order?.latest_refund?.amount ?? "N/A"}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1.25 }}>
                        <strong>Refunded At:</strong> {order?.latest_refund?.refunded_at ? new Date(order.latest_refund.refunded_at).toLocaleString() : "N/A"}
                      </Typography>
                    </>
                  )}
                  
                  {(order.payment_status?.name || order.payment_status || '').toString().toLowerCase() === 'refunded' && (
                    <>
                      <Typography variant="body2" sx={{ mb: 1.25 }}>
                        <strong>Refunded Amount:</strong> {order.refunded_amount ?? order.payment_status?.refunded_amount ?? 'N/A'}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1.25 }}>
                        <strong>Refunded At:</strong> {(() => {
                          const dt = order.refunded_at ?? order.payment_status?.refunded_at;
                          if (!dt) return 'N/A';
                          try {
                            const date = new Date(dt);
                            return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
                          } catch (e) {
                            return String(dt);
                          }
                        })()}
                      </Typography>
                    </>
                  )}

                  {order?.latest_refund && !order?.latest_refund?.is_success && (
                    <Button size="small" variant="outlined" color="primary" onClick={(e) => { e.stopPropagation(); handleCheckPaymentStatus(order); }} disabled={checkingPaymentOrderId === (order?.order_id ?? order?.id)} sx={{ mt: 1.5, textTransform: 'none', fontWeight: 600 }}>
                      {checkingPaymentOrderId === (order?.order_id ?? order?.id) ? (<CircularProgress size={18} sx={{ mr: 0.5 }} />) : null}
                      Check refund status
                    </Button>
                  )}
                </DetailCard>
              </Grid>

              {order.advance_payment && (
                <Grid item xs={12} md={4} sx={{ minWidth: 0 }}>
                  <DetailCard elevation={2} sx={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: 'background.paper', border: `1px solid ${alpha(theme.palette.divider, 0.5)}`, borderRadius: 3, p: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Payment sx={{ mr: 1, color: theme.palette.warning.main }} />
                      <Typography variant="h6" fontWeight={600}>
                        {order.advance_payment.is_full_payment ? "Full Payment" : "Advance Payment"}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>{order.advance_payment.is_full_payment ? "Total Amount:" : "Advance Amount:"}</strong> {order.advance_payment.advance_amount}
                    </Typography>
                    {!order.advance_payment.is_full_payment && (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Remaining Amount:</strong> {order.advance_payment.remaining_amount}
                      </Typography>
                    )}
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Method:</strong> {order.advance_payment.method?.name || "N/A"}
                    </Typography>
                    <Divider sx={{ my: 2 }} />
                    {order.advance_payment.fields && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="h5" fontWeight={600} gutterBottom>Additional Details</Typography>
                        {order.advance_payment.fields.cash_receipt_number && <Typography variant="body2"><strong>Cash Receipt No:</strong> {order.advance_payment.fields.cash_receipt_number}</Typography>}
                        {order.advance_payment.fields.receiver_name && <Typography variant="body2"><strong>Receiver Name:</strong> {order.advance_payment.fields.receiver_name}</Typography>}
                        {order.advance_payment.fields.transaction_id && <Typography variant="body2"><strong>Transaction ID:</strong> {order.advance_payment.fields.transaction_id}</Typography>}
                        {order.advance_payment.fields.upi_id_used && <Typography variant="body2"><strong>UPI ID:</strong> {order.advance_payment.fields.upi_id_used}</Typography>}
                        {order.advance_payment.fields.transaction_reference_no && <Typography variant="body2"><strong>Reference No:</strong> {order.advance_payment.fields.transaction_reference_no}</Typography>}
                        {order.advance_payment.fields.sender_bank_name && <Typography variant="body2"><strong>Sender Bank:</strong> {order.advance_payment.fields.sender_bank_name}</Typography>}
                        {order.advance_payment.fields.bank_name_branch && <Typography variant="body2"><strong>Bank Branch:</strong> {order.advance_payment.fields.bank_name_branch}</Typography>}
                        {order.advance_payment.fields.cheque_number && <Typography variant="body2"><strong>Cheque No:</strong> {order.advance_payment.fields.cheque_number}</Typography>}
                        {order.advance_payment.fields.cheque_date && <Typography variant="body2"><strong>Cheque Date:</strong> {order.advance_payment.fields.cheque_date}</Typography>}
                        {order.advance_payment.fields.cheque_amount && <Typography variant="body2"><strong>Cheque Amount:</strong> {order.advance_payment.fields.cheque_amount}</Typography>}
                      </Box>
                    )}
                  </DetailCard>
                </Grid>
              )}

              {order.delivery_challan && (
                <Grid item xs={12} md={3.8} sx={{ minWidth: 0 }}>
                  <DetailCard elevation={2} sx={{ backgroundColor: 'background.paper', border: `1px solid ${alpha(theme.palette.divider, 0.5)}`, borderRadius: 3, p: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <ChallanIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                      <Typography variant="h6" fontWeight={600}>Delivery Challan Details</Typography>
                    </Box>
                    {order.delivery_challan.vehicle_number && <Typography variant="body2" sx={{ mb: 1 }}><strong>Vehicle Number:</strong> {order.delivery_challan.vehicle_number}</Typography>}
                    {order.delivery_challan.vehicle_name && <Typography variant="body2" sx={{ mb: 1 }}><strong>Vehicle Name:</strong> {order.delivery_challan.vehicle_name}</Typography>}
                    {order.delivery_challan.driver_name && <Typography variant="body2" sx={{ mb: 1 }}><strong>Driver Name:</strong> {order.delivery_challan.driver_name}</Typography>}
                    {order.delivery_challan.dispatch_person_name && <Typography variant="body2" sx={{ mb: 1 }}><strong>Dispatch Person:</strong> {order.delivery_challan.dispatch_person_name}</Typography>}
                    {order.order_by && <Typography variant="body2" sx={{ mb: 1 }}><strong>Order By:</strong> {order.order_by}</Typography>}
                    {order.order_by_entity && <Typography variant="body2" sx={{ mb: 1 }}><strong>Entity Name:</strong> {order.order_by_entity}</Typography>}
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>Kilometer Information</Typography>
                    {order.delivery_challan.start_km !== null && order.delivery_challan.start_km !== undefined && <Typography variant="body2" sx={{ mb: 1 }}><strong>Start KM:</strong> {order.delivery_challan.start_km}</Typography>}
                    {order.delivery_challan.end_km !== null && order.delivery_challan.end_km !== undefined && <Typography variant="body2" sx={{ mb: 1 }}><strong>End KM:</strong> {order.delivery_challan.end_km}</Typography>}
                    {order.delivery_challan.total_km !== null && order.delivery_challan.total_km !== undefined && <Typography variant="body2" sx={{ mb: 1 }}><strong>Total KM:</strong> {order.delivery_challan.total_km}</Typography>}
                  </DetailCard>
                </Grid>
              )}

              <Grid item xs={12} md={8}></Grid>

              <Grid item xs={7.8}>
                <DetailCard elevation={2} sx={{ backgroundColor: 'background.paper', border: `1px solid ${alpha(theme.palette.divider, 0.5)}`, borderRadius: 3, p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <ShoppingCart sx={{ mr: 1, color: theme.palette.primary.main }} />
                    <Typography variant="h5" fontWeight={600}>Order Items ({order.total_items})</Typography>
                  </Box>
                  
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, width: '80px' }}>Sr. No.</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>SKU</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Unit Price</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Quantity</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>GST Rate</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Total</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {order.items.map((item, index) => (
                          <TableRow key={index} sx={{ '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.04) } }}>
                            <TableCell>{index + 1}</TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ maxWidth: 220, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontWeight: 500 }} title={item.product_name}>
                                {item.product_name}
                              </Typography>
                            </TableCell>
                            <TableCell><Typography variant="body2" color="text.secondary">{item.product_sku}</Typography></TableCell>
                            <TableCell align="right">{formatCurrency(item.unit_price)}</TableCell>
                            <TableCell align="right">{parseFloat(item.quantity)}</TableCell>
                            <TableCell align="right">{parseFloat(item.gst_rate)}%</TableCell>
                            <TableCell align="right"><Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>{formatCurrency(item.item_total_with_gst)}</Typography></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Total Items:</strong> {order.total_items}</Typography>
                      <Typography variant="body2"><strong>Total Quantity:</strong> {parseFloat(order.total_quantity)}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Subtotal:</strong> {formatCurrency(order.subtotal)}</Typography>
                        <Typography variant="body2" sx={{ mb: 0.5 }}><strong>GST Amount:</strong> {formatCurrency(order.gst_amount)}</Typography>
                        <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Delivery Charge:</strong> {formatCurrency(order.delivery_charge)}</Typography>
                        {parseFloat(order.discount_amount) > 0 && <Typography variant="body2" color="success.main" sx={{ mb: 0.5 }}><strong>Discount:</strong> -{formatCurrency(order.discount_amount)}</Typography>}
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="h6" color="primary.main" fontWeight={700}>Total: {formatCurrency(order.total_amount)}</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </DetailCard>
              </Grid>
            </Grid>
          </Box>
        </Collapse>
      </StyledTableCell>
    </ExpandableRow>
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress size={40} />
      </Box>
    )
  }

  return (
    <Box sx={{ width: "100%", p: 2 }}>
      {/* Export Controls */}
      {selectedProducts.length > 0 && (
        <Box sx={{ mb: 2, p: 2, backgroundColor: alpha(theme.palette.primary.main, 0.1), borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="body1" fontWeight={600}>
              {selectedProducts.length} product{selectedProducts.length > 1 ? 's' : ''} selected
            </Typography>
            {getEligibleProductsCount() > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1, display: 'block' }}>
                {getEligibleProductsCount()} item Selected
              </Typography>
            )}
          </Box>
          <Box>
            <Button variant="contained" size="small" startIcon={isDownloading ? <CircularProgress size={20} sx={{ color: "#ffffff" }} /> : <FileDownload />} onClick={openActionsMenu} sx={{ textTransform: 'none' }} disabled={isDownloading}>
              {isDownloading ? (<span style={{ color: '#fff7f7', fontWeight: 'bold' }}>Downloading..</span>) : ('Actions')}
            </Button>
            <Menu anchorEl={actionsAnchorEl} open={isActionsMenuOpen} onClose={closeActionsMenu} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
              <ListSubheader disableSticky>Export</ListSubheader>
              <MenuItem onClick={() => handleDownload('excel', onExport)} disabled={isDownloading}>
                <ListItemIcon><FileDownload fontSize="small" /></ListItemIcon>
                <ListItemText primary="Export to Excel" />
              </MenuItem>
              <MenuItem onClick={() => handleDownload('csv', onExport)} disabled={isDownloading}>
                <ListItemIcon><FileDownload fontSize="small" /></ListItemIcon>
                <ListItemText primary="Export to CSV" />
              </MenuItem>
              <Divider />
              <ListSubheader disableSticky>Documents ZIP</ListSubheader>
              <MenuItem onClick={() => handleDownload('invoice', onZipDownload)} disabled={shouldDisableDocumentsAndEmail() || isDownloading}>
                <ListItemIcon><ReceiptIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Invoices ZIP" />
              </MenuItem>
              <MenuItem onClick={() => handleDownload('challan', onZipDownload)} disabled={shouldDisableDocumentsAndEmail() || isDownloading}>
                <ListItemIcon><ChallanIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Challans ZIP" />
              </MenuItem>
              <MenuItem onClick={() => handleDownload('both', onZipDownload)} disabled={shouldDisableDocumentsAndEmail() || isDownloading}>
                <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Both ZIP" />
              </MenuItem>
              <Divider />
              <ListSubheader disableSticky>Email</ListSubheader>
              <MenuItem onClick={handleOpenEmailDialog} disabled={shouldDisableDocumentsAndEmail() || isDownloading}>
                <ListItemIcon><EmailIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Send via Email" />
              </MenuItem>
              <Divider />
              <ListSubheader disableSticky>Operations</ListSubheader>
              <MenuItem onClick={handleOpenBulkPaymentStatusModal} disabled={selectedProducts.length === 0 || isDownloading}>
                <ListItemIcon><Payment fontSize="small" /></ListItemIcon>
                <ListItemText primary="Bulk Payment Status Update" />
              </MenuItem>
              <MenuItem onClick={() => { closeActionsMenu(); handleBulkDeliveryChallan(); }} disabled={selectedProducts.length === 0 || ['Returned', 'Cancelled'].includes(statusFilter) || isDownloading}>
                <ListItemIcon><DeliveryIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary={statusFilter === 'Dispatch' ? 'Status Update' : 'Bulk Delivery Status'} />
              </MenuItem>
            </Menu>
          </Box>
        </Box>
      )}

      <TableContainer component={Paper} sx={(theme) => ({ maxHeight: 600, overflowX: "auto", borderRadius: 2, scrollbarWidth: "thin", scrollbarColor: theme.palette.mode === "dark" ? "#555 #1e1e1e" : "#c1c1c1 #f1f1f1", "&::-webkit-scrollbar": { height: 8, width: 8 }, "&::-webkit-scrollbar-track": { background: theme.palette.mode === "dark" ? "#1e1e1e" : "#f1f1f1", borderRadius: 8 }, "&::-webkit-scrollbar-thumb": { background: theme.palette.mode === "dark" ? "#555" : "#c1c1c1", borderRadius: 8 }, "&::-webkit-scrollbar-thumb:hover": { background: theme.palette.mode === "dark" ? "#777" : "#a8a8a8" } })}>
        <Table stickyHeader>
          <TableHead>
            <TableRow sx={{ backgroundColor: (theme) => theme.palette.grey[100] }}>
              <StyledTableCell sx={{ width: 50, backgroundColor: (theme) => theme.palette.grey[100] }}>
                <Checkbox checked={isAllSelected} indeterminate={selectedProducts.length > 0 && !isAllSelected} onChange={handleSelectAllToggle} color="primary" />
              </StyledTableCell>
              <StyledTableCell sx={{ width: 80, backgroundColor: (theme) => theme.palette.grey[100] }}>Sr. No.</StyledTableCell>
              <StyledTableCell sx={{ backgroundColor: (theme) => theme.palette.grey[100] }}>Order ID</StyledTableCell>
              <StyledTableCell sx={{ backgroundColor: (theme) => theme.palette.grey[100] }}>Customer</StyledTableCell>
              <StyledTableCell sx={{ backgroundColor: (theme) => theme.palette.grey[100] }}>Status</StyledTableCell>
              <StyledTableCell sx={{ backgroundColor: (theme) => theme.palette.grey[100] }}>Total Amount</StyledTableCell>
              <StyledTableCell sx={{ position: "sticky", right: 0, zIndex: 5, backgroundColor: (theme) => theme.palette.grey[100] }}>Actions</StyledTableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} sx={{ border: 'none' }}>
                  <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                    <Typography color="text.secondary">No Orders found</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              filteredData.map((order, index) => {
                const isExpanded = expandedRows.has(order.id)
                const isSelected = isProductSelected(order.id)
                
                return (
                  <React.Fragment key={order.id}>
                    <StyledTableRow hover selected={isSelected} onClick={() => toggleRow(order.id)}>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox checked={isSelected} onChange={(e) => handleProductSelection(order.id, e.target.checked)} color="primary" size="small" />
                      </TableCell>
                      <StyledTableCell style={{ paddingLeft: "16px" }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>{(currentPage - 1) * pageSize + index + 1}</Typography>
                      </StyledTableCell>
                      <StyledTableCell variant="body2">
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleRow(order.id);
                            }}
                            sx={{
                              p: 0.25,
                              transition: "transform 0.2s",
                              transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
                            }}
                          >
                            <KeyboardArrowRightIcon fontSize="small" />
                          </IconButton>
                          <Typography style={{ fontSize: "14px" }} fontWeight={500}>
                            {order.order_id}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                          {order.order_date_formatted || "Date N/A"}
                        </Typography>
                      </StyledTableCell>
                      <StyledTableCell style={{ fontSize: "14px" }} variant="body2">
                        <Typography variant="body2">{order.user.full_name}</Typography>
                        <Typography variant="caption" color="textSecondary">{order.user.mobile_number}</Typography>
                        {(order.farmer_name || order.advance_payment?.order?.farmer_name) && (
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: theme.palette.info.main, fontWeight: 600, fontStyle: 'italic' }}>
                            Behalf of: {order.farmer_name || order.advance_payment?.order?.farmer_name}
                          </Typography>
                        )}
                      </StyledTableCell>
                      <StyledTableCell>
                        <StatusChip label={order.status.name} status={order.status.category?.toLowerCase()} size="small" />
                      </StyledTableCell>
                      <StyledTableCell>
                        <Typography sx={{ fontWeight: 500, fontSize: "14px" }}>{formatCurrency(order.total_amount)}</Typography>
                      </StyledTableCell>
                      <StyledTableCell onClick={(e) => e.stopPropagation()} sx={(theme) => ({ position: "sticky", right: 0, zIndex: 4, backgroundColor: theme.palette.background.paper })}>
                        <Box display="flex" gap={1}>
                          {showCancelRequestActions && onApproveCancelRequest && onRejectCancelRequest ? (
                            <>
                              <Button size="small" color="success" variant="outlined" disabled={updatingCancelRequestOrderId === (order?.order_id ?? order?.id)} onClick={(e) => { e.stopPropagation(); onApproveCancelRequest(order); }} sx={{ textTransform: 'none', fontWeight: 600 }}>
                                {updatingCancelRequestOrderId === (order?.order_id ?? order?.id) ? (<CircularProgress size={18} />) : ('Accepted')}
                              </Button>
                              <Button size="small" color="error" variant="outlined" disabled={updatingCancelRequestOrderId === (order?.order_id ?? order?.id)} onClick={(e) => { e.stopPropagation(); onRejectCancelRequest(order); }} sx={{ textTransform: 'none', fontWeight: 600 }}>
                                {'Rejected'}
                              </Button>
                            </>
                          ) : showReturnRequestActions && onApproveReturnRequest && onRejectReturnRequest ? (
                            <>
                              <Button size="small" color="success" variant="outlined" disabled={updatingReturnRequestOrderId === (order?.order_id ?? order?.id)} onClick={(e) => { e.stopPropagation(); onApproveReturnRequest(order); }} sx={{ textTransform: 'none', fontWeight: 600 }}>
                                {updatingReturnRequestOrderId === (order?.order_id ?? order?.id) ? (<CircularProgress size={18} />) : ('Accepted')}
                              </Button>
                              <Button size="small" color="error" variant="outlined" disabled={updatingReturnRequestOrderId === (order?.order_id ?? order?.id)} onClick={(e) => { e.stopPropagation(); onRejectReturnRequest(order); }} sx={{ textTransform: 'none', fontWeight: 600 }}>
                                {'Rejected'}
                              </Button>
                            </>
                          ) : (
                            <>
                              {/* Update Status Button - Disabled based on permission */}
                              <Tooltip title={!shouldEnableButton?.('update_status') ? "You don't have permission to update status" : "Update Status"}>
                                <span>
                                  <IconButton 
                                    size="small"
                                    onClick={(e) => { e.stopPropagation(); handleOpenStatusModal(order); }}
                                    disabled={!shouldEnableButton?.('update_status')}
                                    sx={{ opacity: !shouldEnableButton?.('update_status') ? 0.5 : 1 }}
                                  >
                                    <UpdateIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>

                              {/* Update Payment Button - Disabled based on permission */}
                              <Tooltip title={!shouldEnableButton?.('update_payment') ? "You don't have permission to update payment" : "Update Payment"}>
                                <span>
                                  <IconButton 
                                    size="small"
                                    onClick={(e) => { e.stopPropagation(); setPaymentUpdateModal({ open: true, order: order, refundAmount: order?.total_amount ?? '' }); }}
                                    disabled={!shouldEnableButton?.('update_payment')}
                                    sx={{ opacity: !shouldEnableButton?.('update_payment') ? 0.5 : 1 }}
                                  >
                                    <Payment fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>

                              {/* Send Email Button - Disabled based on permission */}
                              <Tooltip title={!shouldEnableButton?.('send_email') ? "You don't have permission to send email" : "Send Email"}>
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => { e.stopPropagation(); openSingleOrderEmail(order); }}
                                    disabled={!shouldEnableButton?.('send_email')}
                                    sx={{ opacity: !shouldEnableButton?.('send_email') ? 0.5 : 1 }}
                                  >
                                    <EmailIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>

                              {/* View Invoice Button - Disabled based on permission */}
                                {extractInvoice(order)?.path && (
                                  <Tooltip title={!shouldEnableButton?.('view_invoice') ? "You don't have permission to view invoice" : "View Invoice"}>
                                    <span>
                                      <IconButton 
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          handleViewInvoice(order)
                                        }}
                                        disabled={!shouldEnableButton?.('view_invoice')}
                                        color="primary"
                                        sx={{
                                          opacity: !shouldEnableButton?.('view_invoice') ? 0.5 : 1
                                        }}
                                      >
                                        <ReceiptIcon fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                )}

                              {/* View Delivery Challan Button - Disabled based on permission */}
                              {extractDeliveryChallan(order)?.path && (
                                <Tooltip title={!shouldEnableButton?.('view_delivery_challan') ? "You don't have permission to view delivery challan" : "View Delivery Challan"}>
                                  <span>
                                    <IconButton 
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        handleViewDeliveryChallan(order)
                                      }}
                                      disabled={!shouldEnableButton?.('view_delivery_challan')}
                                      color="secondary"
                                      sx={{
                                        opacity: !shouldEnableButton?.('view_delivery_challan') ? 0.5 : 1
                                      }}
                                    >
                                      <ChallanIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )}
                            </>
                          )}
                        </Box>
                      </StyledTableCell>
                    </StyledTableRow>
                    <OrderDetailsRow order={order} />
                  </React.Fragment>
                )
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Rest of the modals remain the same - Status Update Modal, Payment Status Update Modal, etc. */}
      {/* Status Update Modal */}
       <Dialog 
        open={statusUpdateModal.open} 
        onClose={handleCloseStatusModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <UpdateIcon color="primary" />
            <Typography variant="h6">Update Order Status</Typography>
          </Box>
          {statusUpdateModal.order && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Order ID: {statusUpdateModal.order.order_id}
            </Typography>
          )}
        </DialogTitle>
        
        <DialogContent
        sx={{ /* Firefox */
          scrollbarWidth: "thin",
          scrollbarColor:
            theme.palette.mode === "dark"
              ? "#555 #1e1e1e"
              : "#c1c1c1 #f1f1f1",
          /* Chrome, Edge, Safari */
          "&::-webkit-scrollbar": {
            width: 8,
          },
          "&::-webkit-scrollbar-track": {
            background:
              theme.palette.mode === "dark" ? "#1e1e1e" : "#f1f1f1",
            borderRadius: 8,
          },
          "&::-webkit-scrollbar-thumb": {
            background:
              theme.palette.mode === "dark" ? "#555" : "#c1c1c1",
            borderRadius: 8,
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background:
              theme.palette.mode === "dark" ? "#777" : "#a8a8a8",
          },
          }}>
          {statusUpdateModal.challanError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {statusUpdateModal.challanError}
            </Alert>
          )}

          {statusUpdateModal.isCreatingChallan && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                Creating delivery challan...
              </Box>
            </Alert>
          )}

        {/* Payment Status Warning */}
        {(() => {
          const paymentStatus = (statusUpdateModal.order?.payment_status?.name || statusUpdateModal.order?.payment_status || '').toLowerCase();
          const isPaid = paymentStatus === 'paid' || paymentStatus === 'outstanding';

          const selectedStatus = (statusUpdateModal.order?.status?.name || statusUpdateModal.order?.status_name || '').toLowerCase();
          const originalStatusName = (statusUpdateModal.originalStatus?.name || '').toLowerCase();
          const originalStatusCategory = (statusUpdateModal.originalStatus?.category || '').toLowerCase();
          const isOriginallyDelivered = originalStatusName === 'delivered' || originalStatusCategory === 'delivered';
          const isReturnedSelected = selectedStatus === 'returned';

          // Show payment warning for unpaid orders, but NOT when user has selected
          // Returned (in that case we rely on the Returned-specific validation message).
          if (!isPaid && selectedStatus !== 'returned' && !(isOriginallyDelivered && isReturnedSelected)) {
            return (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight={600}>
                  Payment Required
                </Typography>
                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                  This order must be paid before it can be moved to any status other than "Orders". 
                  Please update the payment status first.
                </Typography>
              </Alert>
            );
          }
          return null;
        })()}

        <FormControl fullWidth sx={{ mt: 2 }}>
          <InputLabel>Order Status</InputLabel>
          <Select
            value={
              statusUpdateModal.order?.status?.name || statusUpdateModal.order?.status_name || ''
            }
            label="Order Status"
            onChange={(e) => {
              const selectedOption = statusOptions.find(opt => opt.name === e.target.value);
              handleStatusModalChange('order', { 
                ...statusUpdateModal.order, 
                status: {
                  name: e.target.value,
                  category: selectedOption?.category || ''
                },
                status_name: e.target.value
              });
            }}
            disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
          >
            {statusOptions
              .map((option) => {
              // Use original status for validation, not the auto-selected next status
              // This ensures we only show the next sequential status, not skip ahead
              const originalStatusCategory = (statusUpdateModal.originalStatus?.category || '').toLowerCase();
              const originalStatusName = (statusUpdateModal.originalStatus?.name || '').toLowerCase();
              
              // Get current status category and name - try multiple sources (for display)
              let currentStatusCategory = statusUpdateModal.order?.status?.category?.toLowerCase();
              const currentStatusName = statusUpdateModal.order?.status?.name || statusUpdateModal.order?.status_name;
              
              // If category not available, find it from statusOptions by name
              if (!currentStatusCategory && currentStatusName) {
                const currentStatusOption = statusOptions.find(opt => opt.name === currentStatusName);
                currentStatusCategory = currentStatusOption?.category?.toLowerCase() || '';
              }
              
              // Get statuses to disable based on ORIGINAL status (not the auto-selected one)
              const statusesToDisable = getStatusesToDisable(originalStatusCategory, originalStatusName);
              
              // Check payment status
              const paymentStatus = (statusUpdateModal.order?.payment_status?.name || statusUpdateModal.order?.payment_status || '').toLowerCase();
              const isPaid = paymentStatus === 'paid' || paymentStatus === 'outstanding';
              
              // If not paid, only allow "Orders" status (name: "Orders")
              const isOrdersStatus = option.name === 'Orders';
              const shouldDisableByPayment = !isPaid && !isOrdersStatus;
              
              // Check if this option should be disabled
              const optionCategory = (option.category || '').toLowerCase();
              // Only disable if category is valid and in the disabled list
              const shouldDisableBySequence = optionCategory && statusesToDisable.includes(optionCategory);
              const shouldDisable = shouldDisableByPayment || shouldDisableBySequence;
              
              return (
                <MenuItem 
                  key={option.id || option.name} 
                  value={option.name}
                  disabled={shouldDisable}
                  sx={shouldDisable ? { 
                    opacity: 0.5,
                    backgroundColor: theme.palette.grey[100],
                    // '&:hover': {
                    //   backgroundColor: theme.palette.grey[100],
                    //   cursor: 'not-allowed'
                    // }
                  } : {}}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    {getIconForCategory(option.category)}
                    <StatusChip 
                      status={option.category?.toLowerCase()}
                      label={option.name}
                      size="small"
                    />
                    {!shouldDisable && statusUpdateModal.order?.status?.name === option.name && (
                      <CheckCircle 
                        color="primary" 
                        sx={{ 
                          ml: 'auto', 
                          fontSize: '16px' 
                        }} 
                      />
                    )}
                    {shouldDisableByPayment && (
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', fontSize: '0.7rem' }}>
                        Payment required
                      </Typography>
                    )}
                    {shouldDisableBySequence && !shouldDisableByPayment && (
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', fontSize: '0.7rem' }}>
                        Sequential only
                      </Typography>
                    )}
                  </Box>
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>

        

        {/* Conditional reason field for Cancelled status */}
        {(statusUpdateModal.order?.status?.name === 'Cancelled' || statusUpdateModal.order?.status_name === 'Cancelled') && (
          <TextField
            fullWidth
            label="Reason"
            value={statusUpdateModal.cancellationReason}
            onChange={(e) => handleStatusModalChange('cancellationReason', e.target.value)}
            disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
            required
            placeholder="Enter reason for cancellation"
            sx={{ mt: 2 }}
            multiline
            rows={3}
          />
        )}

        {/* Conditional reason field for Returned status */}
        {(statusUpdateModal.order?.status?.name === 'Returned' || statusUpdateModal.order?.status_name === 'Returned') && (
          <TextField
            fullWidth
            label="Reason"
            value={statusUpdateModal.returnReason}
            onChange={(e) => handleStatusModalChange('returnReason', e.target.value)}
            disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
            required
            placeholder="Enter reason for returned"
            sx={{ mt: 2 }}
            multiline
            rows={3}

            
          />
        )}

          {/* Conditional fields for shipping */}
          {(statusUpdateModal.order?.status?.category || statusUpdateModal.order?.status)?.toLowerCase() === 'shipped' && (
            <Box sx={{ mt: 3 }}>
              <Box sx={{ 
                p: 2, 
                backgroundColor: alpha(theme.palette.info.main, 0.1),
                borderRadius: 2,
                mb: 2
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <DeliveryIcon color="info" />
                  <Typography variant="subtitle1" fontWeight={600}>
                    Delivery Information Required
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Please provide delivery details to create the delivery challan
                </Typography>
              </Box>

              <Grid container spacing={2}>

                  <Grid item xs={12}>
                  <FormControl fullWidth required>
                    <InputLabel>Order By</InputLabel>
                    <Select
                      value={statusUpdateModal.orderBy}
                      onChange={(e) => handleStatusModalChange('orderBy', e.target.value)}
                      disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                      label="Order By"
                    >
                      <MenuItem value="">
                        <em>Select order by</em>
                      </MenuItem>
                      {orderByChoices.map((choice) => (
                        <MenuItem key={choice.key} value={choice.key}>
                          {choice.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>


                {/* Entity Name */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Delivery Partner Name"
                    value={statusUpdateModal.entityName}
                    onChange={(e) => handleStatusModalChange('entityName', e.target.value)}
                    disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                    required
                    placeholder="Enter Delivery Partner"
                  />
                </Grid>

             
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Driver/Authorized Name"
                    value={statusUpdateModal.driverName}
                    onChange={(e) => handleStatusModalChange('driverName', e.target.value)}
                    disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                    required
                    placeholder="Driver/Authorized Name"
                    error={statusUpdateModal.challanError.includes('Driver name')}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Vehicle No / Reference No"
                    value={statusUpdateModal.vehicleNo}
                    onChange={(e) => handleStatusModalChange('vehicleNo', e.target.value.toUpperCase())}
                    disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                    required
                    placeholder="Vehicle No / Reference No"
                    error={statusUpdateModal.challanError.includes('vehicle number')}
                  />
                </Grid>

                {/* Start KM - only show when Order By is "Truck" */}
                {String(statusUpdateModal.orderBy || '').trim().toLowerCase() === 'truck' && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Start KM"
                      type="number"
                      value={statusUpdateModal.start_km}
                      onChange={(e) => handleStatusModalChange('start_km', e.target.value)}
                      disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                      placeholder="Enter starting kilometer reading"
                    />
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Box>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={statusUpdateModal.useCustomDispatchPerson}
                          onChange={(e) => handleStatusModalChange('useCustomDispatchPerson', e.target.checked)}
                          disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                        />
                      }
                      label="Add custom dispatch person"
                    />
                    {statusUpdateModal.useCustomDispatchPerson ? (
                      <TextField
                        fullWidth
                        label="Custom Dispatch Person Name"
                        value={statusUpdateModal.customDispatchPerson}
                        onChange={(e) => handleStatusModalChange('customDispatchPerson', e.target.value)}
                        disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                        required
                        placeholder="Enter custom dispatch person name"
                        sx={{ mt: 1 }}
                      />
                    ) : (
                      <FormControl fullWidth required sx={{ mt: 1 }}>
                        <InputLabel>Dispatch Person Name</InputLabel>
                        <Select
                          value={statusUpdateModal.dispatchPersonName}
                          label="Dispatch Person Name"
                          onChange={(e) => handleStatusModalChange('dispatchPersonName', e.target.value)}
                          disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                        >
                          <MenuItem value="">
                            <em>Select dispatch person</em>
                          </MenuItem>
                          {dispatchPersons.map((person) => (
                            <MenuItem key={person.id} value={person.name}>
                              {person.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </Box>
                </Grid>
                  <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Delivery Date"
                  type="date"
                  value={statusUpdateModal.shippedDate}
                  onChange={(e) => handleStatusModalChange('shippedDate', e.target.value)}
                  disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                  required
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min: new Date().toISOString().split("T")[0], // 🔑 disables all past dates
                  }}
                />
              </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={statusUpdateModal.generateInvoice}
                        onChange={(e) => handleStatusModalChange('generateInvoice', e.target.checked)}
                        disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                      />
                    }
                    label="Generate Invoice along with Delivery Challan"
                  />
                </Grid>
              </Grid>

              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                * A delivery challan will be created automatically with these details
                {statusUpdateModal.generateInvoice && (
                  <><br />* An invoice will also be generated for this order.</>
                )}
              </Typography>
            </Box>
          )}

          {/* End KM - shown when Start KM is present (optional) */}
          {/* {(String(statusUpdateModal.start_km || statusUpdateModal.order?.delivery_challan?.start_km || '').trim() !== '') && (
            <Box sx={{ mt: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="End KMss"
                    type="number"
                    value={statusUpdateModal.end_km}
                    onChange={(e) => {
                      const endKmValue = e.target.value;
                      handleStatusModalChange('end_km', endKmValue);
                      // Validate End KM > Start KM
                      const startKm = parseFloat(statusUpdateModal.start_km);
                      const endKm = parseFloat(endKmValue);
                      if (!isNaN(startKm) && !isNaN(endKm) && endKm <= startKm) {
                        // Error will be shown via button disabled state and alert on submit
                      }
                    }}
                    disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                    placeholder="Enter ending kilometer reading"
                    error={(() => {
                      const startKmFromModal = statusUpdateModal.start_km;
                      const startKmFromOrder = statusUpdateModal.order?.delivery_challan?.start_km;
                      const startKm = parseFloat(startKmFromModal || startKmFromOrder);
                      const endKm = parseFloat(statusUpdateModal.end_km);
                      return !isNaN(startKm) && !isNaN(endKm) && endKm <= startKm;
                    })()}
                    helperText={(() => {
                      const startKmFromModal = statusUpdateModal.start_km;
                      const startKmFromOrder = statusUpdateModal.order?.delivery_challan?.start_km;
                      const startKm = parseFloat(startKmFromModal || startKmFromOrder);
                      const endKm = parseFloat(statusUpdateModal.end_km);
                      if (!isNaN(startKm) && !isNaN(endKm) && endKm <= startKm) {
                        return 'End KM must be greater than Start KM';
                      }
                      return '';
                    })()}
                  />
                </Grid>
              </Grid>
            </Box>
          )} */}

          {(
  (String(
    statusUpdateModal.start_km ||
    statusUpdateModal.order?.delivery_challan?.start_km ||
    ''
  ).trim() !== '') &&
  (statusUpdateModal.status?.toLowerCase() === 'delivered' ||
   statusUpdateModal.order?.status?.name?.toLowerCase() === 'delivered')
) && (
  <Box sx={{ mt: 3 }}>
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="End KM"
          type="number"
          value={statusUpdateModal.end_km}
          onChange={(e) => {
            const endKmValue = e.target.value;
            handleStatusModalChange('end_km', endKmValue);
          }}
          disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
          placeholder="Enter ending kilometer reading"
          error={(() => {
            const startKm =
              parseFloat(
                statusUpdateModal.start_km ||
                statusUpdateModal.order?.delivery_challan?.start_km
              );
            const endKm = parseFloat(statusUpdateModal.end_km);
            return !isNaN(startKm) && !isNaN(endKm) && endKm <= startKm;
          })()}
          helperText={(() => {
            const startKm =
              parseFloat(
                statusUpdateModal.start_km ||
                statusUpdateModal.order?.delivery_challan?.start_km
              );
            const endKm = parseFloat(statusUpdateModal.end_km);
            if (!isNaN(startKm) && !isNaN(endKm) && endKm <= startKm) {
              return 'End KM must be greater than Start KM';
            }
            return '';
          })()}
        />
      </Grid>
    </Grid>
  </Box>
)}

        </DialogContent>
        
        <DialogActions>
          <Button 
            onClick={handleCloseStatusModal}
            disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
          >
            Cancel
          </Button>
          <Button 
            onClick={() => handleStatusUpdate(
              statusUpdateModal.order?.id,
              statusUpdateModal.order?.status?.name || statusUpdateModal.order?.status_name,
              statusUpdateModal.order?.order_id
            )}
            variant="contained"
            disabled={isSingleStatusSubmitDisabled()}
            startIcon={
              updatingStatus || statusUpdateModal.isCreatingChallan ? 
              <CircularProgress size={16} /> : 
              (statusUpdateModal.order?.status?.category || '').toLowerCase() === 'shipped' ? <DeliveryIcon /> : <UpdateIcon />
            }
          >
            {updatingStatus ? 'Updating Status...' : 
             statusUpdateModal.isCreatingChallan ? 'Creating Challan...' :
             (statusUpdateModal.order?.status?.category || '').toLowerCase() === 'shipped' ? 'Ship Order' : 'Update Status'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Status Update Modal */}
       <Dialog 
        open={paymentUpdateModal.open} 
        onClose={() => setPaymentUpdateModal({ open: false, order: null, refundAmount: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Update Payment Status
          {paymentUpdateModal.order && (
            <Typography variant="body2" color="text.secondary">
              Order ID: {paymentUpdateModal.order.order_id}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Payment Status</InputLabel>
            <Select
              value={(paymentUpdateModal.order?.payment_status?.name || paymentUpdateModal.order?.payment_status || '').toLowerCase()}
              label="Payment Status"
              onChange={(e) => {
                const newStatus = e.target.value;
                setPaymentUpdateModal(prev => ({
                  ...prev,
                  order: { 
                    ...prev.order, 
                    payment_status: { 
                      ...prev.order?.payment_status, 
                      name: newStatus 
                    }
                  },
                  refundAmount: newStatus === 'refunded' ? (prev.order?.total_amount ?? prev.refundAmount ?? '') : prev.refundAmount
                }));
              }}
            >
              {paymentStatusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <PaymentStatusChip 
                    label={option.label} 
                    status={option.value}
                    size="small"
                    sx={{ mr: 1 }}
                  />
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {(paymentUpdateModal.order?.payment_status?.name || paymentUpdateModal.order?.payment_status || '')
            .toLowerCase() === 'refunded' && (() => {
              const maxAmount = paymentUpdateModal.order?.total_amount || 0;
              const refundAmount = Number(paymentUpdateModal.refundAmount || 0);
              const isExceeding = refundAmount > maxAmount;

              const totalAmount = Number(paymentUpdateModal.order?.total_amount ?? paymentUpdateModal.order?.total ?? 0);
              const paidAmount = Number(
                paymentUpdateModal.advance_payment?.paid_amount ??
                paymentUpdateModal.order?.amount_paid ??
                paymentUpdateModal.order?.paid ?? 0
              );
              const pendingAmount = Math.max(0, totalAmount - paidAmount);
              const formatAmount = (n) =>
                Number.isNaN(n) ? '0.00' : n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

              return (
                <Box sx={{ mt: 2 }}>
                  {
                    (() => {
                      const advanceAmount = Number(
                        paymentUpdateModal.order?.advance_payment?.advance_amount ??
                        paymentUpdateModal.order?.advance_amount ?? 0
                      );
                      const remainingAdvance = Number(
                        paymentUpdateModal.order?.advance_payment?.remaining_amount ??
                        paymentUpdateModal.order?.remaining_amount ?? 0
                      );

                  //     return (
                  //       <Box sx={{ mb: 1, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  //          <TextField
                  //   fullWidth
                  //   label="Refund Amount"
                  //   type="number"
                  //   value={paymentUpdateModal.refundAmount ?? ''}
                  //   placeholder="Enter refund amount"
                  //   inputProps={{
                  //     min: 0,
                  //     max: maxAmount,
                  //     step: 0.01,
                  //   }}
                  //   error={isExceeding}
                  //   helperText={
                  //     isExceeding
                  //       ? "You can’t enter more than the actual amount"
                  //       : ''
                  //   }
                  //   onChange={(e) =>
                  //     setPaymentUpdateModal((prev) => ({
                  //       ...prev,
                  //       refundAmount: e.target.value,
                  //     }))
                  //   }
                  // />
                  //         <Typography variant="body2" color="text.secondary">
                  //           Total: ₹{formatAmount(totalAmount)}
                  //         </Typography>
                  //         {/* <Typography variant="body2" color="text.secondary">
                  //           Paid: ₹{formatAmount(paidAmount)}
                  //         </Typography>
                  //         <Typography variant="body2" color="text.secondary">
                  //           Pending: ₹{formatAmount(pendingAmount)}
                  //         </Typography> */}
                  //         <Typography variant="body2" color="text.secondary">
                  //           Paid: ₹{formatAmount(advanceAmount)}
                  //         </Typography>
                  //         <Typography variant="body2" color="text.secondary">
                  //           Pending: ₹{formatAmount(remainingAdvance)}
                  //         </Typography>
                  //       </Box>
                  //     );
                  return (
                    <Box sx={{ mb: 1, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                       <TextField
                fullWidth
                label="Refund Amount"
                type="number"
                value={paymentUpdateModal.refundAmount ?? ''}
                placeholder="Enter refund amount"
                inputProps={{
                  min: 0,
                  max: maxAmount,
                  step: 0.01,
                }}
                error={isExceeding}
                helperText={
                  isExceeding
                    ? "You can’t enter more than the actual amount"
                    : ''
                }
                onChange={(e) =>
                  setPaymentUpdateModal((prev) => ({
                    ...prev,
                    refundAmount: e.target.value,
                  }))
                }
              />
                      <Box display="flex" gap={2} mt={2}>
                     <TextField
                       label="Total"
                    value={formatAmount(totalAmount)}
                      size="small"
                      fullWidth
                         disabled
                       />

                       <TextField
                      label="Paid"
                     value={formatAmount(advanceAmount)}
                     size="small"
                     fullWidth
                     disabled
                     />

                  <TextField
                    label="Pending"
                     value={formatAmount(remainingAdvance)}
                      size="small"
                     fullWidth
                     disabled
                      />
                     </Box>

                </Box>
                  );
                })()
                  }

                 
                </Box>
              );
            })()
          }


        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setPaymentUpdateModal({ open: false, order: null, refundAmount: '' })}
            disabled={updatingPayment}
          >
            Cancel
          </Button>
          <Button 
            onClick={() => handlePaymentStatusUpdate(
              paymentUpdateModal.order?.id,
              paymentUpdateModal.order?.payment_status?.name || paymentUpdateModal.order?.payment_status,
              paymentUpdateModal.order?.order_id,
              paymentUpdateModal.refundAmount,
              paymentUpdateModal.order?.total_amount
            )}
            variant="contained"
            disabled={updatingPayment}
            startIcon={updatingPayment ? <CircularProgress size={16} /> : <UpdateIcon />}
          >
            {updatingPayment ? 'Updating...' : 'Update Status'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invoice View Modal */}
      <InvoiceModal open={invoiceModal.open} onClose={handleCloseInvoiceModal} maxWidth={false} fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PdfIcon color="error" />
              <Typography variant="h6" fontWeight={600}>Invoice - {invoiceModal.product?.product_name}</Typography>
            </Box>
            <IconButton onClick={handleCloseInvoiceModal}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, height: '100%' }}>
          {invoiceModal.invoice && invoiceModal.invoice.path && <InvoiceFrame src={invoiceModal.invoice.path} title="Invoice PDF" onError={(e) => { console.error('Error loading PDF:', e); alert('Error loading invoice PDF. Please try downloading instead.'); }} />}
        </DialogContent>
      </InvoiceModal>

      {/* Delivery Challan View Modal */}
      <InvoiceModal open={deliveryChallanModal.open} onClose={handleCloseDeliveryChallanModal} maxWidth={false} fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ChallanIcon color="secondary" />
              <Typography variant="h6" fontWeight={600}>Delivery Challan - {deliveryChallanModal.product?.product_name}</Typography>
            </Box>
            <IconButton onClick={handleCloseDeliveryChallanModal}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, height: '100%' }}>
          {deliveryChallanModal.challan && deliveryChallanModal.challan.path && <InvoiceFrame src={deliveryChallanModal.challan.path} title="Delivery Challan PDF" onError={(e) => { console.error('Error loading PDF:', e); alert('Error loading delivery challan PDF. Please try downloading instead.'); }} />}
        </DialogContent>
      </InvoiceModal>

      {/* Bulk Delivery Challan Modal */}
       <Dialog
        open={bulkChallanModal.open}
        onClose={handleCloseBulkChallanModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DeliveryIcon color="secondary" />
            <Typography variant="h6" fontWeight={600}>
              Bulk Status Update & Delivery Management
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Update status for {getSelectedProductsCount()} selected orders. For "Dispatch" status, delivery challans will be created.
          </Typography>
        </DialogTitle>

        <DialogContent>
          {bulkChallanModal.error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {bulkChallanModal.error}
            </Alert>
          )}

          {bulkChallanModal.isCreating && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                Creating bulk delivery challan...
              </Box>
            </Alert>
          )}

{(() => {
            // Check payment status for all selected orders
            const selectedOrders = filteredData.filter(order => selectedProducts.includes(order.id));
            const unpaidOrders = selectedOrders.filter(order => {
              const paymentStatus = (order.payment_status?.name || order.payment_status_name || order.payment_status || '').toLowerCase();
              const isPaid = paymentStatus === 'paid' || paymentStatus === 'outstanding';
              return !isPaid;
            });
            
            if (unpaidOrders.length > 0) {
              return (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight={600}>
                    Payment Required
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ mt: 0.5, mb: 2 }}>
                    {unpaidOrders.length} of {selectedOrders.length} selected order{selectedOrders.length > 1 ? 's' : ''} must be paid before status can be updated. 
                    Please update the payment status first. Only unpaid orders will be updated.
                  </Typography>
                  {/* <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    <FormControl sx={{ minWidth: 200 }} size="small">
                      <InputLabel>Update Payment Status To</InputLabel>
                      <Select
                        value={bulkChallanModal.bulkPaymentStatus || ''}
                        label="Update Payment Status To"
                        onChange={(e) => handleBulkChallanInputChange('bulkPaymentStatus', e.target.value)}
                        disabled={isUpdatingBulkPayment || bulkChallanModal.isCreating}
                      >
                        {paymentStatusChoices
                          .filter(choice => {
                            // Only show "paid" and "outstanding" options for this modal
                            const value = (choice.value || choice.key || choice.name || '').toLowerCase();
                            return value === 'paid' || value === 'outstanding';
                          })
                          .map((choice) => {
                            const value = choice.value || choice.key || choice.name || '';
                            const label = choice.label || choice.name || value;
                            return (
                              <MenuItem key={value} value={value}>
                                <PaymentStatusChip 
                                  label={label} 
                                  status={value.toLowerCase()}
                                  size="small"
                                  sx={{ mr: 1 }}
                                />
                                {label}
                              </MenuItem>
                            );
                          })}
                      </Select>
                    </FormControl>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={handleBulkPaymentStatusUpdate}
                      disabled={!bulkChallanModal.bulkPaymentStatus || isUpdatingBulkPayment || bulkChallanModal.isCreating}
                      startIcon={isUpdatingBulkPayment ? <CircularProgress size={16} /> : <Payment />}
                      sx={{ mt: 0.5 }}
                    >
                      {isUpdatingBulkPayment ? 'Updating...' : 'Update Payment Status'}
                    </Button>
                  </Box> */}
                </Alert>
              );
            }
            return null;
          })()}

          <Box sx={{ mt: 2 }}>
            {/* <Box sx={{ 
              p: 2, 
              backgroundColor: alpha(theme.palette.secondary.main, 0.1),
              borderRadius: 2,
              mb: 3
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <ChallanIcon color="secondary" />
                <Typography variant="subtitle1" fontWeight={600}>
                  Delivery Information
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                This information will be used for all selected orders
              </Typography>
            </Box> */}

            <Grid container spacing={2}>
              <Grid item xs={12}>
             

              {/* <FormControl fullWidth>
                <InputLabel>Update Status Tosdfgh</InputLabel>
                <Select
                  value={
                    // If Dispatch tab is active and Dispatch is selected, clear it
                    (statusFilter === 'Dispatch' && bulkChallanModal.selectedStatus === 'Dispatch') 
                      ? '' 
                      : (bulkChallanModal.selectedStatus || '')
                  }
                  label="Update Status To"
                  onChange={(e) => {
                    const selectedValue = e.target.value;
                    const selectedOption = statusOptions.find(opt => opt.name === selectedValue);
                    
                    // Prevent selecting Dispatch when Dispatch tab is active
                    if (statusFilter === 'Dispatch' && selectedValue === 'Dispatch') {
                      return; // Don't allow selection
                    }
                    
                    handleBulkChallanInputChange('selectedStatus', selectedValue);
                    handleBulkChallanInputChange('selectedStatusCategory', selectedOption?.category || '');
                  }}
                  disabled={false}
                >
                  {statusOptions.map((option) => {
                    // Find minimum status among selected orders for sequential validation
                    const selectedOrders = filteredData.filter(order => selectedProducts.includes(order.id));
                    const statusOrder = getStatusOrder();
                    
                    // Check payment status for all selected orders
                    const hasUnpaidOrders = selectedOrders.some(order => {
                      const paymentStatus = (order.payment_status?.name || order.payment_status || '').toLowerCase();
                      const isPaid = paymentStatus === 'paid' || paymentStatus === 'outstanding';
                      return !isPaid;
                    });
                    
                    // If not paid, only allow "Orders" status (name: "Orders")
                    const isOrdersStatus = option.name === 'Orders';
                    const shouldDisableByPayment = hasUnpaidOrders && !isOrdersStatus;
                    
                    // Get the minimum status order (lowest sequence) among selected orders
                    let minStatusOrder = 999;
                    selectedOrders.forEach(order => {
                      const orderStatusCategory = (order.status?.category || '').toLowerCase();
                      const orderStatusName = order.status?.name || '';
                      
                      // Try to find category from statusOptions if not directly available
                      let orderCategory = orderStatusCategory;
                      if (!orderCategory && orderStatusName) {
                        const orderStatusOption = statusOptions.find(opt => opt.name === orderStatusName);
                        orderCategory = orderStatusOption?.category?.toLowerCase() || '';
                      }
                      
                      const orderSequence = statusOrder[orderCategory] || 0;
                      if (orderSequence > 0 && orderSequence < minStatusOrder) {
                        minStatusOrder = orderSequence;
                      }
                    });
                    
                    // If no valid status found, allow all (safety fallback)
                    if (minStatusOrder === 999) {
                      minStatusOrder = 0;
                    }
                    
                    // Always allow Cancelled and Returned (check by category)
                    const alwaysAvailableCategories = ['cancelled', 'returned'];
                    const optionCategory = (option.category || '').toLowerCase();
                    const isAlwaysAvailable = alwaysAvailableCategories.includes(optionCategory);
                    
                    // Get the sequence for this option
                    const optionSequence = statusOrder[optionCategory] || 0;
                    
                    // Disable if:
                    // 1. Payment required (except for "Orders" status)
                    // 2. It's before the minimum status (can't go backwards)
                    // 3. Never disable Cancelled or Returned
                    // 4. If current statusFilter is "Dispatch", disable "Dispatch" option (can only go to Delivered, Returned, or Cancelled)
                    // Note: For bulk operations, we allow jumping forward to any status (not just next sequential)
                    const isDispatchTab = statusFilter === 'Dispatch';
                    const isDispatchOption = option.name === 'Dispatch';
                    const shouldDisableByDispatchTab = isDispatchTab && isDispatchOption;
                    
                    const shouldDisableBySequence = !isAlwaysAvailable && (
                      optionSequence < minStatusOrder || 
                      shouldDisableByDispatchTab
                    );
                    
                    const shouldDisable = shouldDisableByPayment || shouldDisableBySequence;
                    
                    return (
                     <MenuItem 
                        key={option.id || option.name} 
                        value={option.name}
                       disabled={shouldDisable}
                      >

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                          {getIconForCategory(option.category)}
                          <StatusChip 
                            status={option.category?.toLowerCase()}
                            label={option.name}
                            size="small"
                          />
                          {shouldDisableByPayment && (
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', fontSize: '0.7rem' }}>
                              Payment required
                            </Typography>
                          )}
                          {shouldDisableBySequence && !shouldDisableByPayment && (
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', fontSize: '0.7rem' }}>
                              {shouldDisableByDispatchTab ? 'Not available' : 'Sequential only'}
                            </Typography>
                          )}
                        </Box>
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl> */}
{(() => {
  const selectedOrders = filteredData.filter(order => selectedProducts.includes(order.id));
  const hasUnpaidOrders = selectedOrders.some(order => {
    const paymentStatus = (order.payment_status?.name || order.payment_status || '').toLowerCase();
    return !(paymentStatus === 'paid' || paymentStatus === 'outstanding');
  });

  return (
    <>
      {/* {hasUnpaidOrders && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight={600}>
            Payment Required
          </Typography>
          <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
            Some selected orders must be paid before they can be moved to any status other than "Orders". Please update the payment status first.
          </Typography>
        </Alert>
      )} */}

      <FormControl fullWidth>
        <InputLabel>Update Status To</InputLabel>
        <Select
          value={
            (statusFilter === 'Dispatch' && bulkChallanModal.selectedStatus === 'Dispatch')
              ? ''
              : (bulkChallanModal.selectedStatus || '')
          }
          label="Update Status To"
          onChange={(e) => {
            const selectedValue = e.target.value;
            const selectedOption = statusOptions.find(opt => opt.name === selectedValue);

            // Prevent selecting Dispatch when Dispatch tab is active
            if (statusFilter === 'Dispatch' && selectedValue === 'Dispatch') return;

            handleBulkChallanInputChange('selectedStatus', selectedValue);
            handleBulkChallanInputChange('selectedStatusCategory', selectedOption?.category || '');
          }}
        >
          {statusOptions.map((option) => {
            // Use the same sequential validation logic as individual modal
            const statusOrder = getStatusOrder();
            const isDeliveredTab = (statusFilter || '').toLowerCase() === 'delivered';

            // Payment check
            const isOrdersStatus = option.name === 'Orders';
            const shouldDisableByPayment = hasUnpaidOrders && !isOrdersStatus;

            // Sequential validation - check each selected order individually
            // For bulk operations, we need to check if ALL selected orders can move to this status
            // A status is only available if it's the next sequential status for ALL selected orders
            let isAvailableForAllOrders = true;
            const optionCategory = (option.category || '').toLowerCase();
            const optionName = (option.name || '').toLowerCase();
            
            // Check each selected order
            for (const order of selectedOrders) {
              const orderStatusCategory = (order.status?.category || '').toLowerCase();
              const orderStatusName = (order.status?.name || '').toLowerCase();
              
              // Get statuses to disable for this order (using same logic as individual modal)
              const statusesToDisable = getStatusesToDisable(orderStatusCategory, orderStatusName);
              
              // Check if this option should be disabled for this order
              const shouldDisableForThisOrder = statusesToDisable.includes(optionCategory);
              
              if (shouldDisableForThisOrder) {
                isAvailableForAllOrders = false;
                break; // If any order can't use this status, disable it
              }
            }

            // Additional checks for Dispatch tab
            const isDispatchTab = statusFilter === 'Dispatch';
            const isDispatchOption = option.name === 'Dispatch';
            const shouldDisableByDispatchTab = isDispatchTab && isDispatchOption;

            // Combine all disable conditions
            const isReturnedOption = optionName === 'returned' || optionCategory === 'returned';
            const shouldDisableByDeliveredTab = isDeliveredTab && !isReturnedOption;
            const shouldDisableBySequence = !isAvailableForAllOrders || shouldDisableByDispatchTab || shouldDisableByDeliveredTab;
            const shouldDisable = shouldDisableByPayment || shouldDisableBySequence;

            return (
              <MenuItem
                key={option.id || option.name}
                value={option.name}
                disabled={shouldDisable}
                sx={shouldDisable ? { opacity: 0.5, backgroundColor: theme.palette.grey[100] } : {}}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  {getIconForCategory(option.category)}
                  <StatusChip status={option.category?.toLowerCase()} label={option.name} size="small" />
                  {shouldDisableByPayment && (
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', fontSize: '0.7rem' }}>
                      Payment required
                    </Typography>
                  )}
                  {shouldDisableBySequence && !shouldDisableByPayment && (
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', fontSize: '0.7rem' }}>
                      {shouldDisableByDispatchTab || shouldDisableByDeliveredTab ? 'Not available' : 'Sequential only'}
                    </Typography>
                  )}
                </Box>
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    </>
  );
})() }

              </Grid>
              
              {/* Show delivery fields only for Dispatch status */}
              {bulkChallanModal.selectedStatus === 'Dispatch' && (
                <>
                  <Grid item xs={12}>
                    <Box sx={{ 
                      p: 2, 
                      backgroundColor: alpha(theme.palette.warning.main, 0.1),
                      borderRadius: 2,
                      mb: 2
                    }}>
                      <Typography variant="subtitle2" fontWeight={600} color="warning.main">
                        Delivery Information Required for Dispatch Status
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Please provide delivery details to create delivery challans
                      </Typography>
                    </Box>
                  </Grid>
                  
                  {/* Order By - 1st in sequence (bulk modal) */}
                  <Grid item xs={12}>
                    <FormControl fullWidth required>
                      <InputLabel>Order By</InputLabel>
                      <Select
                        value={bulkChallanModal.orderBy}
                        onChange={(e) => handleBulkChallanInputChange('orderBy', e.target.value)}
                        disabled={bulkChallanModal.isCreating}
                        label="Order By"
                      >
                        <MenuItem value="">
                          <em>Select order by</em>
                        </MenuItem>
                        {orderByChoices.map((choice) => (
                          <MenuItem key={choice.key} value={choice.key}>
                            {choice.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Entity Name - Dynamic based on Order By selection - 2nd in sequence */}
                  <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Delivery Partner Name"
                    value={bulkChallanModal.entityName}
                    onChange={(e) => handleBulkChallanInputChange('entityName', e.target.value)}
                    disabled={bulkChallanModal.isCreating}
                    required
                    placeholder="Enter Delivery Partner"
                  />
                </Grid>

                  {/* Driver/Authorized Name - 3rd in sequence */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Driver/Authorized Name"
                      value={bulkChallanModal.driverName || ''}
                      onChange={(e) => handleBulkChallanInputChange('driverName', e.target.value)}
                      disabled={bulkChallanModal.isCreating}
                      required
                      placeholder="Enter driver's full name"
                      error={bulkChallanModal.error && bulkChallanModal.error.includes('Driver name')}
                    />
                  </Grid>
                  
                  {/* Vehicle No / Reference No - 4th in sequence */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Vehicle No / Reference No"
                      value={bulkChallanModal.vehicleNo || ''}
                      onChange={(e) => handleBulkChallanInputChange('vehicleNo', e.target.value.toUpperCase())}
                      disabled={bulkChallanModal.isCreating}
                      required
                      placeholder="Enter vehicle registration number"
                      error={bulkChallanModal.error && bulkChallanModal.error.includes('vehicle number')}
                    />
                  </Grid>

                  {/* Start KM - only show when Order By is "Truck" */}
                  {String(bulkChallanModal.orderBy || '').trim().toLowerCase() === 'truck' && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Start KM"
                        type="number"
                        value={bulkChallanModal.start_km || ''}
                        onChange={(e) => {
                          const value = e.target.value;

                          // Allow only digits
                          if (/^\d*$/.test(value)) {
                            handleBulkChallanInputChange('start_km', value);
                          }
                        }}
                        onKeyDown={(e) => {
                          // Block unwanted keys
                          if (['e', 'E', '+', '-', '.'].includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        disabled={bulkChallanModal.isCreating}
                        placeholder="Enter starting kilometer reading"
                      />
                    </Grid>
                  )}
                  
                  <Grid item xs={12}>
                    <Box>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={bulkChallanModal.useCustomDispatchPerson}
                            onChange={(e) => handleBulkChallanInputChange('useCustomDispatchPerson', e.target.checked)}
                            disabled={bulkChallanModal.isCreating}
                          />
                        }
                        label="Add custom dispatch person"
                      />
                      {bulkChallanModal.useCustomDispatchPerson ? (
                        <TextField
                          fullWidth
                          label="Custom Dispatch Person Name"
                          value={bulkChallanModal.customDispatchPerson}
                          onChange={(e) => handleBulkChallanInputChange('customDispatchPerson', e.target.value)}
                          disabled={bulkChallanModal.isCreating}
                          placeholder="Enter custom dispatch person name"
                          sx={{ mt: 1 }}
                        />
                      ) : (
                        <FormControl fullWidth sx={{ mt: 1 }}>
                          <InputLabel>Dispatch Person Name</InputLabel>
                          <Select
                            value={bulkChallanModal.dispatchPersonName}
                            label="Dispatch Person Name"
                            onChange={(e) => handleBulkChallanInputChange('dispatchPersonName', e.target.value)}
                            disabled={bulkChallanModal.isCreating}
                          >
                            <MenuItem value="">
                              <em>Select dispatch person</em>
                            </MenuItem>
                            {dispatchPersons.map((person) => (
                              <MenuItem key={person.id} value={person.name}>
                                {person.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Delivery Date"
                      type="date"
                      value={bulkChallanModal.deliveryDate || new Date().toISOString().slice(0,10)}
                      onChange={(e) => handleBulkChallanInputChange('deliveryDate', e.target.value)}
                      disabled={bulkChallanModal.isCreating}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Email Addresses (Optional)
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                        <TextField
                          fullWidth
                          size="small"
                          value={bulkChallanModal.emailInput || ''}
                          onChange={(e) => handleBulkChallanInputChange('emailInput', e.target.value)}
                          disabled={bulkChallanModal.isCreating}
                          placeholder="Enter email address"
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              addEmailToBulkModal();
                            }
                          }}
                        />
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={addEmailToBulkModal}
                          disabled={!bulkChallanModal.emailInput?.trim() || bulkChallanModal.isCreating}
                        >
                          Add
                        </Button>
                      </Box>
                      {bulkChallanModal.emails && bulkChallanModal.emails.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {bulkChallanModal.emails.map((email, index) => (
                            <Chip
                              key={index}
                              label={email}
                              onDelete={() => removeEmailFromBulkModal(email)}
                              size="small"
                              disabled={bulkChallanModal.isCreating}
                            />
                          ))}
                        </Box>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        Delivery challans will be sent to these email addresses
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={bulkChallanModal.generateInvoice || false}
                          onChange={(e) => handleBulkChallanInputChange('generateInvoice', e.target.checked)}
                          disabled={bulkChallanModal.isCreating}
                        />
                      }
                      label="Generate Invoice along with Delivery Challan"
                    />
                  </Grid>
                </>
              )}

              {/* End KM field for Delivered status - only when any selected order has Start KM */}
              {bulkChallanModal.selectedStatus === 'Delivered' && (() => {
                const selectedOrders = filteredData.filter(order => selectedProducts.includes(order.id));
                const anySelectedOrderHasStartKm = selectedOrders.some(order => {
                  const startKm = order.delivery_challan?.start_km;
                  return startKm !== null && startKm !== undefined && String(startKm).trim() !== '';
                });
                return anySelectedOrderHasStartKm;
              })() && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="End KM"
                    // required
                    type="number"
                    value={bulkChallanModal.end_km || ''}
                    onChange={(e) => {
                      const endKmValue = e.target.value;
                      handleBulkChallanInputChange('end_km', endKmValue);
                      // Validate End KM > Start KM
                      const startKm = parseFloat(bulkChallanModal.start_km);
                      const endKm = parseFloat(endKmValue);
                      if (!isNaN(startKm) && !isNaN(endKm) && endKm <= startKm) {
                        // Error will be shown via button disabled state and alert on submit
                      }
                    }}
                    disabled={bulkChallanModal.isCreating}
                    placeholder="Enter ending kilometer reading"
                    error={(() => {
                      const startKm = parseFloat(bulkChallanModal.start_km);
                      const endKm = parseFloat(bulkChallanModal.end_km);
                      return !isNaN(startKm) && !isNaN(endKm) && endKm <= startKm;
                    })()}
                    helperText={(() => {
                      const startKm = parseFloat(bulkChallanModal.start_km);
                      const endKm = parseFloat(bulkChallanModal.end_km);
                      if (!isNaN(startKm) && !isNaN(endKm) && endKm <= startKm) {
                        return 'End KM must be greater than Start KM';
                      }
                      return '';
                    })()}
                  />
                </Grid>
              )}

              {bulkChallanModal.selectedStatus === 'Cancelled' && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Reason"
                    value={bulkChallanModal.cancellationReason || ''}
                    onChange={(e) => handleBulkChallanInputChange('cancellationReason', e.target.value)}
                    disabled={bulkChallanModal.isCreating}
                    required
                    placeholder="Enter reason for cancellation"
                    sx={{ mt: 2 }}
                    multiline
                    rows={3}
                  />
                </Grid>
              )}

              {bulkChallanModal.selectedStatus === 'Returned' && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Reason"
                    value={bulkChallanModal.returnReason || ''}
                    onChange={(e) => handleBulkChallanInputChange('returnReason', e.target.value)}
                    disabled={bulkChallanModal.isCreating}
                    required
                    placeholder="Enter reason for returned"
                    sx={{ mt: 2 }}
                    multiline
                    rows={3}
                  />
                </Grid>
              )}
            </Grid>

           
          </Box>
        </DialogContent>

        <DialogActions>
          <Button 
            onClick={handleCloseBulkChallanModal}
            disabled={bulkChallanModal.isCreating}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleBulkChallanSubmit}
            variant="contained"
            color="secondary"
            disabled={isBulkSubmitDisabled()}
            startIcon={
              bulkChallanModal.isCreating ? 
              <CircularProgress size={16} /> : 
              <DeliveryIcon />
            }
            sx={{
              '&:hover': {
                backgroundColor: theme.palette.success.main,
              },
              transition: 'all 0.2s ease-in-out'
            }}
          >
            {bulkChallanModal.isCreating ? 'Processing...' : 
              bulkChallanModal.selectedStatus === 'Dispatch' ? 
                `Create ${getEligibleProductsCount()} Challans` : 
                `Update ${getSelectedProductsCount()} Orders`
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email Dialogs */}
      <Dialog
        open={emailDialog.open}
        onClose={handleCloseEmailDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmailIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>Send Documents via Email</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Selected orders: {filteredData.filter(p => selectedProducts.includes(p.id)).length}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {emailDialog.error && (
            <Alert severity="error" sx={{ mb: 2 }}>{emailDialog.error}</Alert>
          )}
          {emailDialog.success && (
            <Alert severity="success" sx={{ mb: 2 }}>{emailDialog.success}</Alert>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Emails"
                placeholder="Enter email addresses separated by commas"
                value={emailDialog.emails}
                onChange={(e) => handleEmailDialogChange('emails', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <FormControlLabel
                  control={<Checkbox checked={emailDialog.sendInvoice} onChange={(e) => handleEmailDialogChange('sendInvoice', e.target.checked)} />}
                  label={`Include Invoices (${getAvailableCounts().invoices} available)`}
                />
                <FormControlLabel
                  control={<Checkbox checked={emailDialog.sendDelivery} onChange={(e) => handleEmailDialogChange('sendDelivery', e.target.checked)} />}
                  label={`Include Delivery Challans (${getAvailableCounts().challans} available)`}
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEmailDialog} disabled={emailDialog.isSending}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={emailDialog.isSending ? <CircularProgress size={16} /> : <SendIcon />}
            disabled={emailDialog.isSending}
            onClick={handleEmailSendSubmit}
            sx={{ textTransform: 'none' }}
          >
            {emailDialog.isSending ? 'Sending...' : 'Send Email'}
          </Button>
        </DialogActions>
      </Dialog>

      <AppOrderPopup open={singleEmailDialog.open} onClose={closeSingleOrderEmail} maxWidth="sm" fullWidth>
        <DialogTitle>Send Documents via Email</DialogTitle>
        <DialogContent>
          <Stack spacing={2.25} sx={{ mt: 1 }}>
            {singleEmailDialog.order && (
              <Typography variant="body2" color="text.secondary">
                Order ID: {singleEmailDialog.order.order_id}
              </Typography>
            )}

            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Add Email"
                value={singleEmailDialog.emailInput}
                onChange={(e) => setSingleEmailDialog(prev => ({ ...prev, emailInput: e.target.value, error: '', success: '' }))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') addEmailToSingle()
                }}
              />
              <Button
                variant="contained"
                onClick={addEmailToSingle}
                sx={{ minWidth: 92, textTransform: 'none' }}
                startIcon={<AddIcon />}
              >
                Add
              </Button>
            </Box>

            {singleEmailDialog.emails.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {singleEmailDialog.emails.map((email) => (
                  <Chip
                    key={email}
                    label={email}
                    onDelete={() => removeEmailFromSingle(email)}
                    size="small"
                    variant="outlined"
                    color="primary"
                  />
                ))}
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={singleEmailDialog.sendInvoice}
                    onChange={(e) => setSingleEmailDialog(prev => ({ ...prev, sendInvoice: e.target.checked, error: '', success: '' }))}
                    disabled={!extractInvoice(singleEmailDialog.order)?.path}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">include Invoice</Typography>
                    {!extractInvoice(singleEmailDialog.order)?.path && (
                      <Typography variant="caption" color="text.secondary">
                        (not available)
                      </Typography>
                    )}
                  </Box>
                }
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={singleEmailDialog.sendDelivery}
                    onChange={(e) => setSingleEmailDialog(prev => ({ ...prev, sendDelivery: e.target.checked, error: '', success: '' }))}
                    disabled={!extractDeliveryChallan(singleEmailDialog.order)?.path}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">Include Delivery Challan</Typography>
                    {!extractDeliveryChallan(singleEmailDialog.order)?.path && (
                      <Typography variant="caption" color="text.secondary">
                        (not available)
                      </Typography>
                    )}
                  </Box>
                }
              />
            </Box>

            {singleEmailDialog.error && (
              <Alert severity="error" onClose={() => setSingleEmailDialog(prev => ({ ...prev, error: '' }))}>
                {singleEmailDialog.error}
              </Alert>
            )}

            {singleEmailDialog.success && (
              <Alert severity="success" onClose={() => setSingleEmailDialog(prev => ({ ...prev, success: '' }))}>
                {singleEmailDialog.success}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeSingleOrderEmail} disabled={singleEmailDialog.isSending}>Cancel</Button>
          <Button variant="contained" startIcon={singleEmailDialog.isSending ? <CircularProgress size={16} /> : <SendIcon />} disabled={singleEmailDialog.isSending || (!extractInvoice(singleEmailDialog.order)?.path && !extractDeliveryChallan(singleEmailDialog.order)?.path)} onClick={submitSingleOrderEmail}>
            {singleEmailDialog.isSending ? 'Sending...' : 'Send Email'}
          </Button>
        </DialogActions>
      </AppOrderPopup>

      {/* Bulk Payment Status Update Modal */}
       <Dialog
        open={bulkPaymentStatusModal.open}
        onClose={handleCloseBulkPaymentStatusModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Update Payment Status
          <Typography variant="body2" color="text.secondary">
            {getSelectedProductsCount()} selected order{getSelectedProductsCount() > 1 ? 's' : ''}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {bulkPaymentStatusModal.error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {bulkPaymentStatusModal.error}
            </Alert>
          )}

          {bulkPaymentStatusModal.isUpdating && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                Updating payment status...
              </Box>
            </Alert>
          )}

          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Payment Status</InputLabel>
            <Select
              value={bulkPaymentStatusModal.selectedPaymentStatus || ''}
              label="Payment Status"
              onChange={(e) => setBulkPaymentStatusModal(prev => ({
                ...prev,
                selectedPaymentStatus: e.target.value,
                error: ''
              }))}
              disabled={bulkPaymentStatusModal.isUpdating}
            >
              {paymentStatusChoices.length > 0 ? (
                paymentStatusChoices.map((choice) => {
                  const value = choice.value || choice.key || choice.name || '';
                  const label = choice.label || choice.name || value;
                  return (
                    <MenuItem key={value} value={value}>
                      <PaymentStatusChip 
                        label={label} 
                        status={value.toLowerCase()}
                        size="small"
                        sx={{ mr: 1 }}
                      />
                      {label}
                    </MenuItem>
                  );
                })
              ) : (
                // Fallback to hardcoded options if API hasn't loaded
                paymentStatusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <PaymentStatusChip 
                      label={option.label} 
                      status={option.value}
                      size="small"
                      sx={{ mr: 1 }}
                    />
                    {option.label}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCloseBulkPaymentStatusModal}
            disabled={bulkPaymentStatusModal.isUpdating}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleBulkPaymentStatusUpdateFromModal}
            variant="contained"
            disabled={!bulkPaymentStatusModal.selectedPaymentStatus || bulkPaymentStatusModal.isUpdating}
            startIcon={bulkPaymentStatusModal.isUpdating ? <CircularProgress size={16} /> : <UpdateIcon />}
          >
            {bulkPaymentStatusModal.isUpdating ? 'Updating...' : 'Update Payment'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default ProductList;